/**
 * N8N Data Transformers
 *
 * Utilities to transform N8N response payloads into UI-compatible data structures
 */

import {
  CampaignMetrics,
  LeadCardResponse,
  GetLeadResponse,
} from '../types/n8nResponses';
import {
  CampaignMetadata,
  MinimalLeadData,
  LeadCardData,
} from '../types';
import { OptimalLead } from '../types/optimalSchema';
import { extractExperienceYearsFromSummary, extractFollowersFromSummary, getAppwriteStorageUrl } from './helpers';
import { getNormalizedStatusKey } from './statusMapping';
import { normalizeLocale, getLanguageName } from '../constants/locales';
import { parseISRNotesFromRecord } from './isrNotes';

// Dev-only log batching for the company-logo constructor below: collapse the
// per-lead "constructed" lines into one summary per transform run.
let logoConstructionCount = 0;
let logoConstructionTick: number | null = null;

// ============================================================================
// CAMPAIGN TRANSFORMERS
// ============================================================================

const normalizeCampaignKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/\(ai\)/g, '')
    .replace(/[^a-z0-9]+/g, '');

const extractCampaignText = (value: any): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    for (const item of value) {
      const extracted = extractCampaignText(item);
      if (extracted) return extracted;
    }
    return null;
  }
  if (typeof value === 'object') {
    for (const key of ['value', 'text', 'content', 'answer', 'fieldValue', 'url']) {
      const extracted = extractCampaignText(value[key]);
      if (extracted) return extracted;
    }
  }
  return null;
};

const getCustomCampaignField = (campaign: Record<string, any>, fieldNames: string[]): string | null => {
  const targets = fieldNames.map(normalizeCampaignKey);
  const containers = [
    campaign.custom_fields,
    campaign.customFields,
    campaign.customfields,
    campaign.custom_fields_data,
  ].filter(Boolean);

  for (const container of containers) {
    if (Array.isArray(container)) {
      for (const field of container) {
        if (!field || typeof field !== 'object') continue;
        const name = extractCampaignText(field.name ?? field.key ?? field.label ?? field.fieldName);
        if (!name || !targets.includes(normalizeCampaignKey(name))) continue;
        const value = extractCampaignText(field.value ?? field.text ?? field.content ?? field.answer ?? field.fieldValue);
        if (value) return value;
      }
      continue;
    }

    if (typeof container !== 'object') continue;
    for (const [key, value] of Object.entries(container)) {
      if (!targets.includes(normalizeCampaignKey(key))) continue;
      const extracted = extractCampaignText(value);
      if (extracted) return extracted;
    }
  }

  return null;
};

const getCampaignText = (
  campaign: Record<string, any>,
  directKeys: string[],
  customFieldNames: string[] = []
) => {
  for (const key of directKeys) {
    const extracted = extractCampaignText(campaign[key]);
    if (extracted) return extracted;
  }
  return getCustomCampaignField(campaign, customFieldNames);
};

const getCampaignRaw = (
  campaign: Record<string, any>,
  directKeys: string[],
  customFieldNames: string[] = []
) => {
  for (const key of directKeys) {
    if (campaign[key] !== null && campaign[key] !== undefined) return campaign[key];
  }
  return getCustomCampaignField(campaign, customFieldNames);
};

const normalizeOutreachChannels = (value: any) => {
  if (Array.isArray(value)) {
    const channels = value
      .map((channel) => {
        if (typeof channel === 'string') return { value: channel.trim(), id: channel.trim() };
        const label = extractCampaignText(channel);
        return label ? { value: label, id: channel?.id || label } : null;
      })
      .filter(Boolean);
    return channels.length > 0 ? channels : null;
  }

  const text = extractCampaignText(value);
  if (!text) return null;
  return text
    .split(',')
    .map((channel) => channel.trim())
    .filter(Boolean)
    .map((channel) => ({ value: channel, id: channel }));
};

/**
 * Transform N8N CampaignMetrics to UI CampaignMetadata
 */
export function transformCampaignMetrics(n8nCampaign: CampaignMetrics): CampaignMetadata {
  const source = n8nCampaign as CampaignMetrics & Record<string, any>;
  const rawCampaign = source.campaign && typeof source.campaign === 'object'
    ? source.campaign as Record<string, any>
    : source as Record<string, any>;
  const rawClient = source.client && typeof source.client === 'object'
    ? source.client as Record<string, any>
    : source as Record<string, any>;

  if (import.meta.env.DEV) {
    console.log('🔍 [TRANSFORM CAMPAIGN] N8N Raw Data:', {
    campaign_id: rawCampaign.campaign_id || rawCampaign.id || rawCampaign.task_id,
    campaign_name: rawCampaign.campaign_name || rawCampaign.name,
    start: rawCampaign.start || rawCampaign.dates?.start,
    done: rawCampaign.done || rawCampaign.dates?.done,
    start_date: rawCampaign.start_date,
    end_date: rawCampaign.end_date,
    weekly_limit: rawCampaign.weekly_limit || getCustomCampaignField(rawCampaign, ['weekly limit']),
    all_keys: Object.keys(rawCampaign)
  });
  // DEBUG: Log specific fields that are reported missing in the UI
  console.log('🔍 [TRANSFORM CAMPAIGN] Field inspection for missing UI data:', {
    generator_id: rawCampaign.generator_id,
    source: rawCampaign.source,
    lead_generator_source: rawCampaign.lead_generator_source,
    primary_campaign_objective: rawCampaign.primary_campaign_objective,
    campaign_goal: rawCampaign.campaign_goal,
    goal: rawCampaign.goal,
    buyer_personas: rawCampaign.buyer_personas,
    target_audience_pain_points: rawCampaign.target_audience_pain_points,
    best_outreach_timing: rawCampaign.best_outreach_timing,
    sales_frameworks: rawCampaign.sales_frameworks,
    success_metrics: rawCampaign.success_metrics,
    usp: rawCampaign.usp,
    unique_sales_point: rawCampaign.unique_sales_point,
    unique_selling_point: rawCampaign.unique_selling_point,
    guarantee: rawCampaign.guarantee,
    offer_pricing: rawCampaign.offer_pricing,
    custom_fields_keys: rawCampaign.custom_fields ? (Array.isArray(rawCampaign.custom_fields) ? rawCampaign.custom_fields.map((f: any) => f?.name || f?.key) : Object.keys(rawCampaign.custom_fields)) : 'none',
  });
  }

  const outreachChannels = normalizeOutreachChannels(
    getCampaignRaw(rawCampaign, ['outreach_channel', 'outreach_channels'], ['Outreach Channel'])
  );

  const result = {
    id: getCampaignText(rawCampaign, ['campaign_id', 'id', 'task_id', 'custom_id']) || '',
    name: getCampaignText(rawCampaign, ['campaign_name', 'name']) || 'Untitled campaign',
    created: getCampaignText(rawCampaign, ['created']) || getCampaignText(rawCampaign.dates || {}, ['created']) || '',
    createdAt: getCampaignText(rawCampaign, ['created']) || getCampaignText(rawCampaign.dates || {}, ['created']) || '',
    updatedAt: getCampaignText(rawCampaign, ['updatedAt', 'updated_at']) || getCampaignText(rawCampaign.dates || {}, ['updated']) || getCampaignText(rawCampaign, ['created']) || '',
    // Prioritize start_date/end_date over legacy start/done fields
    start: getCampaignText(rawCampaign, ['start_date', 'start']) || getCampaignText(rawCampaign.dates || {}, ['start']) || null,
    done: getCampaignText(rawCampaign, ['end_date', 'done']) || getCampaignText(rawCampaign.dates || {}, ['done', 'closed']) || null,
    status: getCampaignText(rawCampaign, ['status']) || null,
    weekly_limit: Number(getCampaignText(rawCampaign, ['weekly_limit'], ['weekly limit'])) || null,
    management_level: null,
    // Normalize to BCP 47 when present; keep null when the campaign didn't specify one.
    preferred_language: (() => {
      const raw = getCampaignText(rawCampaign, ['preferred_language'], ['Preferred Language']);
      return raw ? normalizeLocale(raw) : raw;
    })(),
    best_outreach_timing: getCampaignText(rawCampaign, ['best_outreach_timing'], ['Best Outreach Timing (AI)']),
    buyer_personas: getCampaignText(rawCampaign, ['buyer_personas'], ['Buyer Personas (AI)']),
    desires: getCampaignText(rawCampaign, ['desires', 'campaign_desires'], ['Campaign Desires']),
    meeting_url: getCampaignText(rawCampaign, ['meeting_url', 'campaign_meeting_url'], ['Campaign Meeting URL']),
    summary: getCampaignText(rawCampaign, ['summary', 'campaign_summary'], ['Campaign Summary', 'Campaign Context Text'])
      ? [getCampaignText(rawCampaign, ['summary', 'campaign_summary'], ['Campaign Summary', 'Campaign Context Text'])]
      : null,
    product_website: getCampaignText(rawCampaign, ['product_website'], ['Company/Product Website']),
    competitor_analysis: getCampaignText(rawCampaign, ['competitor_analysis'], ['Competitor Analysis (AI)']),
    core_value_proposition: getCampaignText(rawCampaign, ['core_value_proposition', 'unique_value_proposition', 'value_proposition'], ['Core Value Proposition (AI)']),
    industry_keyworks: getCampaignText(rawCampaign, ['industry_keyworks', 'industry_keywords'], ['Industry Keyworks (AI)']),
    other_notes: getCampaignText(rawCampaign, ['other_notes'], ['Other Notes']),
    outreach_channel: outreachChannels,
    primary_campaign_objective: getCampaignText(rawCampaign, ['primary_campaign_objective', 'campaign_goal', 'goal'], ['Primary Campaign Objective']),
    sales_frameworks: getCampaignRaw(rawCampaign, ['sales_frameworks'], ['Sales Frameworks (AI)']) || null,
    success_metrics: getCampaignText(rawCampaign, ['success_metrics'], ['Success Metrics (AI)']),
    target_audience_pain_points: getCampaignText(rawCampaign, ['target_audience_pain_points'], ['Target Audience Pain Points (AI)']),
    target_audience_niche: getCampaignText(rawCampaign, ['target_audience_niche', 'target_audience _niche'], ['Target Audience Niche (AI)', 'Targeted Companies']),
    leads_to_target: Number(getCampaignText(rawCampaign, ['total_leads', 'leads_to_target'], ['Targeted Leads'])) || null,
    // Per-status counts: prefer the legacy snake_case aggregate, then the new
    // payload's `metrics` block, whose keys are the RAW ClickUp statuses in
    // UPPERCASE ("NEW LEAD", "MESSAGE READY", …). Normalize each key to the
    // canonical snake_case bucket HERE so every consumer (cards, metrics
    // group, pipeline) reads canonical keys regardless of payload shape.
    leads_by_status: (() => {
      if (rawCampaign.leads_by_status && typeof rawCampaign.leads_by_status === 'object') {
        return rawCampaign.leads_by_status;
      }
      const metrics = source.metrics;
      if (!metrics || typeof metrics !== 'object') return null;
      const normalized: Record<string, number> = {};
      for (const [rawKey, value] of Object.entries(metrics as Record<string, unknown>)) {
        const bucket = getNormalizedStatusKey(rawKey);
        const count = Number(value) || 0;
        normalized[bucket] = (normalized[bucket] ?? 0) + count;
      }
      return normalized;
    })(),
    search_location: getCampaignText(rawCampaign, ['search_location']),
    source: getCampaignText(rawCampaign, ['source', 'lead_generator_source'], ['Lead Generator Source']) || getCampaignText(rawCampaign, ['generator_id'], ['Generator ID']),
    block_outreach: rawCampaign.block_outreach ?? null,
    // Outreach execution mode (true=Auto / false=Manual) — null on legacy rows.
    send_outreach: rawCampaign.send_outreach ?? null,
    // String form ("Manual" | "Auto"). The n8n workflow returns it inside the
    // campaign's custom_fields map under the ClickUp select field
    // "Outreach type": { value: "Manual", id: "…" }. Unwrap the { value }
    // object; fall back to direct keys for the flat payload shape.
    send_outreach_mode: (() => {
      const unwrap = (raw: any): string | null => {
        if (raw === null || raw === undefined) return null;
        if (typeof raw === 'object' && typeof raw.value === 'string') return raw.value;
        if (typeof raw === 'string' && raw.trim()) return raw;
        return null;
      };
      const customFields = rawCampaign.custom_fields ?? rawCampaign.customFields;
      if (customFields && typeof customFields === 'object' && !Array.isArray(customFields)) {
        const direct = unwrap((customFields as Record<string, any>)['Outreach type']);
        if (direct) return direct;
      }
      const viaRaw = unwrap(
        getCampaignRaw(rawCampaign, ['send_outreach_mode', 'outreach_mode'], ['Outreach type', 'Outreach mode'])
      );
      if (viaRaw) return viaRaw;
      return rawCampaign.send_outreach_mode ?? rawCampaign.outreach_mode ?? null;
    })(),
    available_outreach_assets: getCampaignRaw(rawCampaign, ['available_outreach_assets', 'outreach_assets'], ['Available Outreach Assets']),
    resources_assigned: rawCampaign.resources_assigned || null,
    usp: getCampaignText(rawCampaign, ['usp']),
    unique_sales_point: getCampaignText(rawCampaign, ['unique_sales_point']),
    unique_selling_point: getCampaignText(rawCampaign, ['unique_selling_point']),
    guarantee: getCampaignText(rawCampaign, ['guarantee', 'offer_guarantee']),
    offer_pricing: getCampaignText(rawCampaign, ['offer_pricing', 'pricing', 'offer_price']),
    campaign_context_file: getCampaignText(rawCampaign, ['campaign_context_file'], ['Campaign Context File']),
    client_email: getCampaignText(rawClient, ['client_email', 'email']) || '',
    client_name: getCampaignText(rawClient, ['client_name', 'name']) || '',
    invited_users: [],
    // Manager: flat key first, then the ClickUp custom field
    // "Account Manager": { value: "<appwrite-uid>", id: "…" }.
    account_manager_id: rawCampaign.account_manager_id
      ?? (() => {
        const customFields = rawCampaign.custom_fields ?? rawCampaign.customFields;
        if (!customFields || typeof customFields !== 'object' || Array.isArray(customFields)) return null;
        const raw = (customFields as Record<string, any>)['Account Manager'];
        if (raw === null || raw === undefined) return null;
        if (typeof raw === 'object' && typeof raw.value === 'string' && raw.value.trim()) return raw.value;
        if (typeof raw === 'string' && raw.trim() && raw.toLowerCase() !== 'null') return raw;
        return null;
      })(),
    seller_lvl1: getCampaignText(rawCampaign, ['seller_lvl1', 'seller_id']) || null,
    seller_name: getCampaignText(rawCampaign, ['seller_name']) || null,
    custom_fields: rawCampaign.custom_fields ?? rawCampaign.customFields ?? rawCampaign.customfields ?? rawCampaign.custom_fields_data ?? null,
  };

  if (import.meta.env.DEV) {
    console.log('✅ [TRANSFORM CAMPAIGN] Transformed Result:', {
      id: result.id,
      name: result.name,
      start: result.start,
      done: result.done,
      // Log all the fields that are reported missing in the UI
      source: result.source,
      primary_campaign_objective: result.primary_campaign_objective,
      buyer_personas: result.buyer_personas,
      target_audience_pain_points: result.target_audience_pain_points,
      best_outreach_timing: result.best_outreach_timing,
      sales_frameworks: result.sales_frameworks,
      success_metrics: result.success_metrics,
      usp: result.usp,
      unique_sales_point: result.unique_sales_point,
      unique_selling_point: result.unique_selling_point,
      guarantee: result.guarantee,
      offer_pricing: result.offer_pricing,
    });
  }

  return result as CampaignMetadata;
}

/**
 * Transform array of N8N CampaignMetrics to UI CampaignMetadata array
 */
export function transformCampaignMetricsArray(
  n8nCampaigns: CampaignMetrics[]
): CampaignMetadata[] {
  return n8nCampaigns.map(transformCampaignMetrics);
}

// ============================================================================
// LEAD CARD TRANSFORMERS (for Grid View)
// ============================================================================

/**
 * Helper: Extract string from value that might be array or string
 */
function extractFieldValue(val: any): string | undefined {
  if (val === null || val === undefined) return undefined;
  if (Array.isArray(val)) {
    return val.length > 0 ? String(val[0]) : undefined;
  }
  if (typeof val === 'object') {
    const statusLikeValue = val.value ?? val.status ?? val.label ?? val.name;
    if (statusLikeValue !== undefined && statusLikeValue !== null) {
      return extractFieldValue(statusLikeValue);
    }
  }
  return String(val);
}

function extractLeadCardStatus(rawLead: any): string | undefined {
  const statusCandidates = [
    rawLead.status,
    rawLead.lead_status,
    rawLead.workflow_status,
    rawLead.person_status,
    rawLead.sequence_status?.status,
    rawLead.lvl2?.status,
    rawLead.lvl1_person?.status,
    rawLead.person?.status,
  ];

  return statusCandidates
    .map((value) => extractFieldValue(value))
    .find((value) => value && value.trim().length > 0);
}

/**
 * Leads arrive from the DB with status + substatus (e.g. "OUTREACHING · not
 * started"). Older rows carry none — the substatus is always optional.
 */
function extractLeadCardSubstatus(rawLead: any): string | undefined {
  const substatusCandidates = [
    rawLead.lead_substatus,
    rawLead.substatus,
    rawLead.lvl2?.lead_substatus,
    rawLead.lvl2?.substatus,
    rawLead.sequence_status?.substatus,
  ];

  return substatusCandidates
    .map((value) => extractFieldValue(value))
    .find((value) => value && value.trim().length > 0);
}

/**
 * Transform N8N LeadCardResponse to UI MinimalLeadData
 * This is used for rendering lead cards in the grid view
 */
export function transformLeadCard(n8nLead: LeadCardResponse): MinimalLeadData {
  // Helper to find field in multiple locations (flat or nested)
  const raw = n8nLead as any;
  const lvl1Comp = raw.lvl1_company || {};
  const lvl1Person = raw.lvl1_person || {};
  const lvl2 = raw.lvl2 || {};

  // Debug: log channel-related fields from first lead (dev only - samples PII)
  if (import.meta.env.DEV && Math.random() < 0.05) {
    const channelKeys = Object.keys(raw).filter(k =>
      /email|phone|mobile|linkedin|whatsapp|instagram|messenger|line|twitter|facebook|tiktok|youtube|discord|wechat|google_places|tripadvisor|website/i.test(k)
    );
    console.log(`📞 [Channel Debug] Lead ${raw.lead_id} channel keys:`, channelKeys.reduce((acc, k) => ({ ...acc, [k]: raw[k] }), {}));
    console.log(`📞 [Channel Debug] Has lvl1_person:`, !!lvl1Person && Object.keys(lvl1Person).length > 0);
    console.log(`📞 [Channel Debug] Has lvl2:`, !!lvl2 && Object.keys(lvl2).length > 0);
  }

  // Extract channel fields from multiple possible locations in the N8N response
  const ch = (...keys: string[]) => {
    for (const k of keys) {
      const v = raw[k] ?? lvl1Person[k] ?? lvl2[k] ?? lvl1Comp[k];
      if (v && typeof v === 'string' && v.trim() && v.trim() !== 'N/A' && v.trim() !== '#') return v;
    }
    return '';
  };

  const industryVal = n8nLead.company_industry || raw.industry || lvl1Comp.industry || lvl2.industry;
  // N8N data uses company_cnae_code/company_cnae_text field names
  const cnaeCodeVal = raw.company_cnae_code || n8nLead.cnae_code || raw.cnaeCode || lvl1Comp.cnae_code || lvl2.cnae_code;
  const cnaeTextVal = raw.company_cnae_text || n8nLead.cnae_text || raw.cnaeText || lvl1Comp.cnae_text || lvl2.cnae_text;
  const continentVal = n8nLead.continent || lvl1Comp.continent;
  const sizeVal = n8nLead.size_range || n8nLead.company_size || lvl1Comp.size_range || lvl2.company_size;
  const employeeVal =
    raw.company_nr_employees ||
    raw.nr_employees ||
    raw.employee_count ||
    lvl1Comp.nr_employees ||
    lvl1Comp.employee_count ||
    lvl2.nr_employees ||
    lvl2.employee_count;
  // Revenue can be: string (formatted), number (raw), or in lvl1_company
  const revenueVal = n8nLead.company_revenue
    ? String(n8nLead.company_revenue)
    : (n8nLead.revenue || lvl1Comp.revenue || lvl1Comp.revenue_range);
  const replacesVal = raw.replaces ?? n8nLead.replaces ?? lvl2.replaces ?? null;
  const replacesReasonVal = raw.replaces_reason ?? n8nLead.replaces_reason ?? lvl2.replaces_reason ?? null;
  const statusVal = extractLeadCardStatus(raw) || 'NEW LEAD';

  // Opportunities for the campaign report sheets (same records the ISR
  // Opportunities tab reads). Only rows with usable prose become cards — the
  // same oppRecords rule the report adapter applies. Absent nested payloads
  // (cards workflow without the columns) yield none, never a crash.
  const oppRecords = (v: any): any[] => {
    let parsed = v;
    if (typeof v === 'string') { try { parsed = JSON.parse(v); } catch { return []; } }
    return Array.isArray(parsed) ? parsed.filter(o => o && typeof o === 'object' && !Array.isArray(o) && String(o.need ?? o.description ?? '').trim()) : [];
  };
  const campaignOpportunities = oppRecords(lvl2?.opportunities);
  const generalOpportunities = oppRecords(lvl1Person?.opportunities);

  return {
    // Identity
    id: n8nLead.lead_id,
    campaignId: n8nLead.campaign_id,
    campaignName: n8nLead.campaign_name,

    // Display fields
    leadName: n8nLead.person_name,
    leadRole: n8nLead.person_role || 'Unknown Role',
    companyName: n8nLead.company_name || 'Unknown Company',
    companyIndustry: extractFieldValue(industryVal) || 'Unknown',
    cnae_code: extractFieldValue(cnaeCodeVal),
    cnae_text: extractFieldValue(cnaeTextVal),
    continent: extractFieldValue(continentVal),
    size_range: extractFieldValue(sizeVal),
    companyEmployees: extractFieldValue(employeeVal),
    companySize: extractFieldValue(sizeVal),
    nr_employees: extractFieldValue(employeeVal),
    company_nr_employees: extractFieldValue(raw.company_nr_employees || lvl1Comp.nr_employees),
    employee_count: extractFieldValue(raw.employee_count || lvl1Comp.employee_count || lvl2.employee_count),
    revenue: extractFieldValue(revenueVal),
    status: statusVal,
    // Substatus from the DB (optional — legacy rows carry none).
    salesSubstatus: extractLeadCardSubstatus(raw) || null,
    icpScore: n8nLead.icp_score,
    // Direct URL to the generated PDF (lvl2.report_lead_url). Non-empty means
    // the report exists — the UI skips the per-lead storage existence probe.
    reportLeadUrl: extractFieldValue(lvl2.report_lead_url) || null,

    // Images (Appwrite Storage URLs - images stored with lvl1_id as filename)
    // FALLBACK: Support both profile_picture_url (new) and profile_picture (legacy)
    profilePicture: n8nLead.profile_picture_url || (n8nLead as any).profile_picture || '',
    profilePictureStorageUrl: n8nLead.profile_picture_url || (n8nLead as any).profile_picture || null,
    companyLogo: (() => {
      // If N8N provides company_logo URL, use it directly
      if (n8nLead.company_logo && n8nLead.company_logo.trim() !== '') {
        return n8nLead.company_logo;
      }

      // Otherwise, construct Appwrite Storage URL
      // Try company_id first (preferred), then fallback to lead_id
      const companyId = n8nLead.company_id;
      const leadId = n8nLead.lead_id;
      const fileId = companyId || leadId;

      if (fileId) {
        const bucketId = 'lead-images'; // Bucket for company logos
        const appwriteUrl = getAppwriteStorageUrl(bucketId, fileId.toString());

        if (import.meta.env.DEV) {
          // One line per TRANSFORM RUN, not per lead — a 500-lead refresh
          // shouldn't print 500 near-identical lines to the console.
          logoConstructionCount += 1;
          if (logoConstructionTick === null) {
            logoConstructionTick = window.setTimeout(() => {
              console.log(`🏢 [N8N Transformer] Company logos constructed: ${logoConstructionCount}`);
              logoConstructionCount = 0;
              logoConstructionTick = null;
            }, 0);
          }
        }

        return appwriteUrl;
      }

      console.warn(`⚠️ [N8N Transformer] No company logo available for lead ${n8nLead.lead_id}`);
      return '';
    })(),

    // Location — the flat city/state/country columns are COMPANY location
    // (the user-confirmed provenance of the list "Location"/HQ column; the
    // separate company_country field agrees). leadCity/State/Country come
    // ONLY from the nested person blocks (lvl2 → lvl1_person) — the lead's
    // own location, present when the cards workflow includes them, empty
    // otherwise. NEVER seeded from the flat columns.
    companyCity: n8nLead.city || '',
    companyState: n8nLead.state || '',
    companyCountry: n8nLead.country || '',
    leadCity: lvl2.city || lvl1Person.city || raw.person_city || '',
    leadState: lvl2.state || lvl1Person.state || raw.person_state || '',
    leadCountry: lvl2.country || lvl1Person.country || raw.person_country || '',

    // Contact
    leadLinkedin: ch('linkedin_id', 'person_linkedin', 'linkedin') || '',
    email: n8nLead.person_email || '',
    // Email verification data — powers the quality-colored email icon in
    // channel icon rows. Small object; worth including in card payloads.
    emailProviderData: safeParse(lvl1Comp.email_provider) || null,
    // Per-channel enrichment timestamps from lvl2 — when a channel's _enriched
    // field is truthy, it was verified by Brainiacs.
    channelEnrichedAt: extractChannelEnrichedAt(lvl2),
    phone: ch('phone_id', 'person_phone', 'phone') || undefined,
    mobile: ch('mobile_id', 'person_mobile', 'mobile') || undefined,
    leadWhatsapp: ch('whatsapp_id', 'whatsapp') || undefined,
    leadInstagram: ch('instagram_id', 'instagram') || undefined,
    leadMessenger: ch('messenger_id', 'messenger') || undefined,
    leadLine: ch('line_id', 'line') || undefined,
    leadTwitter: ch('twitter_x_id', 'twitter_id', 'twitter') || undefined,
    leadFacebook: ch('facebook_id', 'facebook') || undefined,
    leadTiktok: ch('tiktok_id', 'tiktok') || undefined,
    leadYoutube: ch('youtube_id', 'youtube') || undefined,
    leadDiscord: ch('discord_id', 'discord') || undefined,
    leadWechat: ch('wechat_id', 'wechat') || undefined,
    leadGooglePlaces: ch('google_places_id', 'google_places') || undefined,
    leadTripadvisor: ch('tripadvisor_id', 'tripadvisor') || undefined,
    companyEmail: ch('company_email', 'email') || undefined,
    companyPhone: ch('company_phone', 'phone') || undefined,
    companyWebsite: ch('company_website', 'website') || undefined,
    companyLinkedin: ch('company_linkedin', 'linkedin') || undefined,
    companyWhatsapp: ch('company_whatsapp', 'whatsapp') || undefined,
    companyInstagram: ch('company_instagram', 'instagram') || undefined,
    companyMessenger: ch('company_messenger', 'messenger') || undefined,
    companyLine: ch('company_line', 'line') || undefined,
    companyTwitter: ch('company_twitter', 'twitter_x', 'twitter') || undefined,
    companyFacebook: ch('company_facebook', 'facebook') || undefined,
    companyTiktok: ch('company_tiktok', 'tiktok') || undefined,
    companyYoutube: ch('company_youtube', 'youtube') || undefined,
    companyDiscord: ch('company_discord', 'discord') || undefined,
    companyWechat: ch('company_wechat', 'wechat') || undefined,
    companyGooglePlaces: ch('company_google_places', 'google_places') || undefined,
    companyTripadvisor: ch('company_tripadvisor', 'tripadvisor') || undefined,

    // Better Candidate (replaces another lead)
    replaces: extractFieldValue(replacesVal) || null,
    replaces_reason: extractFieldValue(replacesReasonVal) || null,

    // Metadata
    timestamp: n8nLead.created_at,

    campaignOpportunities,
    generalOpportunities,

    // Type flag
    __isMinimal: true,
  };
}

/**
 * Transform array of N8N LeadCardResponse to UI MinimalLeadData array
 */
export function transformLeadCardsArray(
  n8nLeads: LeadCardResponse[]
): MinimalLeadData[] {
  return n8nLeads.map(transformLeadCard);
}

/**
 * Extract per-channel enrichment timestamps from lvl2. A truthy
 * `xx_enriched` column means Brainiacs verified that channel during
 * enrichment — shared by the card and full-lead transformers so the
 * VERIFIED pills read one source of truth.
 */
const ENRICHED_CHANNEL_KEYS = [
  'email_enriched', 'phone_enriched', 'mobile_enriched', 'whatsapp_enriched',
  'linkedin_enriched', 'website_enriched', 'twitter_x_enriched',
  'instagram_enriched', 'messenger_enriched',
] as const;

function extractChannelEnrichedAt(lvl2: any): Record<string, string> | null {
  if (!lvl2) return null;
  const result: Record<string, string> = {};
  for (const key of ENRICHED_CHANNEL_KEYS) {
    const value = lvl2[key];
    if (value && typeof value === 'string' && value.trim()) {
      result[key.replace('_enriched', '')] = value;
    }
  }
  return Object.keys(result).length ? result : null;
}

// ============================================================================
// FULL LEAD TRANSFORMERS (for Tab View)
// ============================================================================

/**
 * Helper: Safely parse JSON string
 */
function safeParse(data: any): any {
  if (!data) return null;
  if (typeof data === 'object') return data;
  if (typeof data === 'string') {
    try {
      return JSON.parse(data);
    } catch {
      return data;
    }
  }
  return data;
}

/**
 * Transform N8N GetLeadResponse to UI OptimalLead (tab-based structure)
 * This is the main transformation for the lead detail view
 *
 * @deprecated Use transformN8NToMappedLead() instead
 * This function creates an intermediate OptimalLead structure that is no longer needed.
 * New code should use transformN8NToMappedLead() for direct N8N → MappedLeadData transformation.
 */
export function transformFullLead(n8nLead: GetLeadResponse): OptimalLead {
  const { lvl2, lvl1_person, lvl1_company } = n8nLead;

  // Parse JSON fields from person
  const profile_intel = safeParse(lvl1_person.profile_intelligence) || {};
  const opportunities = safeParse(lvl1_person.opportunities) || [];
  const recent_activities = safeParse(lvl1_person.recent_activities) || [];
  const person_summary = safeParse(lvl1_person.summary) || {};

  // Parse JSON fields from company
  const company_intelligence = safeParse(lvl1_company?.company_intelligence) || {};
  const key_people = safeParse(lvl1_company?.key_people) || [];
  const recent_news = safeParse(lvl1_company?.recent_news) || [];
  const company_summary = safeParse(lvl1_company?.summary) || {};

  return {
    // ============================================
    // METADATA (Flat, Queryable)
    // ============================================
    id: String(lvl1_person.id),
    campaignId: lvl2.campaign_id,
    status: mapN8NStatusToOptimalStatus(lvl2.status),
    timestamp: lvl2.created_At,
    last_enriched: lvl2.created_At,

    // ============================================
    // QUICK FILTERS (Flat, Indexed)
    // ============================================
    person_name: lvl2.name,
    person_email: lvl2.email_id || '',
    person_role: lvl2.role_position || 'Unknown',
    person_seniority: mapSeniority(lvl1_person.seniority),
    company_name: lvl1_company?.name || person_summary?.company_name || 'Unknown',
    company_industry: lvl1_company?.industry || lvl1_person.industry || '',
    company_country: lvl1_company?.country || lvl2.country,
    icp_score: lvl2.icp_score,
    engagement_score: 0, // TODO: Calculate from activities

    // ============================================
    // TAB DATA (Organized by Modal Tabs)
    // ============================================
    tabs: {
      overview: buildOverviewTab(lvl2, profile_intel, opportunities),
      person: buildPersonTab(lvl2, lvl1_person),
      company: buildCompanyTab(lvl2, lvl1_person, lvl1_company, company_intelligence, company_summary, recent_news, key_people),
      signals: buildSignalsTab(recent_activities),
      network: buildNetworkTab(profile_intel, key_people),
      profile_intel: buildProfileIntelTab(lvl2, profile_intel, opportunities),
      outreach: buildOutreachTab(lvl2),
      interactions: buildInteractionsTab(),
    },

    // ============================================
    // RAW N8N DATA (For backward compatibility)
    // ============================================
    _rawN8NLvl2: lvl2,
    _rawN8NLvl1Person: lvl1_person,
    _rawN8NLvl1Company: lvl1_company,
  };
}

// ============================================================================
// TAB BUILDERS
// ============================================================================

/**
 * Build Overview Tab
 */
function buildOverviewTab(lvl2: any, profile_intel: any, opportunities: any[]): any {
  const icp = lvl2.icp || {};

  return {
    sections: {
      executive_summary: {
        headline: lvl2.overview || 'Lead profile summary',
        description: lvl2.overview || '',
        readiness_score: lvl2.icp_score,
        icp_fit: mapICPFit(lvl2.icp_score),
      },
      key_metrics: {
        icp_score: lvl2.icp_score,
        engagement_score: 0, // TODO: Calculate
        years_experience: profile_intel?.professional_profile?.years_experience || 0,
        company_size: lvl2.nr_employees || 'Unknown',
        decision_authority: profile_intel?.professional_position?.decision_authority || 'Unknown',
      },
      top_opportunities: opportunities.slice(0, 3).map((opp: any) => ({
        id: opp.id,
        type: opp.type,
        category: opp.category,
        amount_usd: opp.amount_usd,
        timeline_months: opp.timeline_months,
        urgency: opp.urgency,
        confidence: opp.confidence,
        need: opp.need,
        evidence: opp.evidence,
        decision_maker: opp.decision_maker,
        keywords: opp.keywords,
      })),
      quick_actions: {
        recommended_channel: getRecommendedChannel(lvl2.personalization_strategy),
        best_time_to_contact: getBestContactTime(lvl2.personalization_strategy),
        key_talking_points: getTalkingPoints(icp),
      },
    },
  };
}

/**
 * Build Person Tab
 * Extracts ALL person-related fields from lvl2 and lvl1_person
 */
function buildPersonTab(lvl2: any, lvl1: any): any {
  return {
    sections: {
      contact_info: {
        email: lvl2.email_id || lvl2.email_enriched || '',
        phone: lvl2.phone_id || lvl2.phone_enriched || null,
        mobile: lvl2.mobile_id || null,
        linkedin_url: lvl2.linkedin_id || lvl2.linkedin_enriched || null,
        website: lvl2.website_id || lvl2.website_enriched || null,
        social_media: {
          linkedin: lvl2.linkedin_id || lvl2.linkedin_enriched || null,
          twitter: lvl2.twitter_x_id || lvl2.twitter_x_enriched || null,
          facebook: lvl2.facebook_id || lvl2.facebook_enriched || null,
          instagram: lvl2.instagram_id || lvl2.instagram_enriched || null,
          youtube: lvl2.youtube_id || lvl2.youtube_enriched || null,
          tiktok: lvl2.tiktok_id || lvl2.tiktok_enriched || null,
          discord: lvl2.discord_id || lvl2.discord_enriched || null,
          pinterest: lvl2.pinterest_id || lvl2.pinterest_enriched || null,
          github: lvl2.github_id || lvl2.github_enriched || null,
          line: lvl2.line_id || null,
          wechat: lvl2.wechat_id || null,
          whatsapp: lvl2.whatsapp_id || lvl2.whatsapp_enriched || null,
          messenger: lvl2.messenger_id || null,
          google_places: lvl2.google_places_id || lvl2.google_places_enriched || null,
          tripadvisor: lvl2.tripadvisor_id || lvl2.tripadvisor_enriched || null,
        },
      },
      professional_info: {
        current_role: lvl2.role_position || lvl1.role_position || 'Unknown',
        department: lvl1.department || null,
        seniority: lvl1.seniority || 'Unknown',
        years_experience: 0, // TODO: Calculate from profile_intelligence
        career_stage: 'ESTABLISHED', // TODO: Derive from seniority
        skills: [], // TODO: Extract from profile_intelligence
        key_responsibilities: [], // TODO: Extract from profile_intelligence
        linkedin_summary: '', // TODO: Extract from profile_intelligence
      },
      location: {
        country: lvl2.country || lvl1.country,
        country_code: lvl1.country_code,
        state: lvl2.state || lvl1.state,
        city: lvl2.city || lvl1.city,
        postal_code: lvl1.postal_code,
        neighborhood: lvl1.neighborhood,
        full_address: lvl2.full_address || lvl1.full_address,
        timezone: lvl2.timezone || undefined,
        plus_code: lvl1.plus_code,
        coordinates: {
          lat: lvl2.lat || lvl1.lat,
          lon: lvl2.lon || lvl1.lon,
        },
      },
      images: {
        profile_picture_url: lvl2.profile_picture || lvl1.profile_picture,
        profile_picture_storage_id: null,
      },
    },
  };
}

/**
 * Build Company Tab
 */
function buildCompanyTab(
  lvl2: any,
  lvl1_person: any,
  lvl1_company: any,
  company_intelligence: any,
  company_summary: any,
  recent_news: any[],
  key_people: any[]
): any {
  return {
    sections: {
      company_profile: {
        name: lvl1_company?.name || company_summary?.company_name || 'Unknown',
        website: lvl1_company?.website || lvl2.website_id || null,
        linkedin_url: (lvl2 as any)?.company_linkedin_id || (lvl2 as any)?.company_linkedin_url || (lvl2 as any)?.company_linkedin || (lvl1_company as any)?.linkedin_id || lvl1_company?.linkedin_url || null,
        industry: lvl1_company?.industry || lvl1_person.industry || '',
        founded_year: lvl1_company?.founded_year || null,
        description: lvl1_company?.description || company_summary?.description || '',
        business_model: lvl1_company?.business_model || 'B2B',
        executive_overview: company_intelligence?.executive_overview || company_summary?.overview || '',
        unique_value_props: company_intelligence?.unique_value_props || [],
        key_products: company_intelligence?.key_products || [],
        target_customers: company_intelligence?.target_customers || [],
      },
      social_media: {
        linkedin: (lvl2 as any)?.company_linkedin_id || (lvl2 as any)?.company_linkedin_url || (lvl2 as any)?.company_linkedin || (lvl1_company as any)?.linkedin_id || lvl1_company?.linkedin_url || null,
        twitter: lvl1_company?.twitter_url || null,
        facebook: lvl1_company?.facebook_url || null,
        instagram: lvl1_company?.instagram_url || null,
        youtube: lvl1_company?.youtube_url || null,
      },
      size_and_stage: {
        employee_count: lvl1_company?.nr_employees || lvl2.nr_employees || 'Unknown',
        size_category: determineSizeCategory(lvl1_company?.nr_employees || lvl2.nr_employees),
        stage: lvl1_company?.funding_status || 'Unknown',
        revenue_range: lvl1_company?.revenue_range || null,
        funding_status: lvl1_company?.funding_status || null,
      },
      location: {
        country: lvl1_company?.country || lvl2.country,
        country_code: lvl1_company?.country_code || lvl1_person.country_code,
        state: lvl1_company?.state || lvl2.state,
        city: lvl1_company?.city || lvl2.city,
        full_address: lvl1_company?.full_address || lvl2.full_address,
        latitude: lvl1_company?.lat || lvl2.lat,
        longitude: lvl1_company?.lon || lvl2.lon,
      },
      intelligence: {
        executive_overview: company_intelligence?.executive_overview || '',
        strategic_focus: company_intelligence?.strategic_focus || [],
        key_competitors: company_intelligence?.key_competitors || [],
        recent_news: recent_news || [],
        pain_points: [lvl2.icp?.top_pain || ''],
        key_people: key_people || [],
      },
      images: {
        logo_url: lvl1_company?.logo_url || null,
        logo_storage_id: null,
      },
    },
  };
}

/**
 * Build Signals Tab
 */
function buildSignalsTab(activities: any[]): any {
  return {
    sections: {
      recent_activities: activities.map((activity: any) => ({
        id: activity.id || Math.random().toString(),
        source: activity.source || 'unknown',
        timestamp: activity.timestamp || new Date().toISOString(),
        content: {
          text: activity.content?.text || '',
          images: activity.content?.images || [],
          engagement: activity.content?.engagement || { likes: 0, comments: 0, shares: 0 },
        },
        analysis: {
          headline: activity.analysis?.headline || '',
          topics: activity.analysis?.topics || [],
          sentiment: activity.analysis?.sentiment || 'neutral',
          relevance_score: activity.analysis?.relevance_score || 0,
          actionable_insight: activity.analysis?.actionable_insight || '',
        },
      })),
      engagement_summary: {
        total_activities: activities.length,
        most_active_platform: 'linkedin', // TODO: Calculate
        avg_engagement_rate: 0,
        trending_topics: [],
      },
    },
  };
}

/**
 * Build Network Tab
 */
function buildNetworkTab(profile_intel: any, key_people: any[]): any {
  const social_presence = profile_intel?.social_presence || {};

  return {
    sections: {
      connections: {
        total_connections: social_presence.linkedin_connections || 0,
        mutual_connections: social_presence.mutual_connections || [],
        key_relationships: key_people || [],
      },
      influence: {
        linkedin_followers: social_presence.linkedin_followers || 0,
        twitter_followers: social_presence.twitter_followers || 0,
        influence_score: social_presence.influence_score || 0,
        thought_leadership_topics: profile_intel?.content_analysis?.primary_topics || [],
      },
    },
  };
}

/**
 * Build Profile Intel Tab
 */
function buildProfileIntelTab(lvl2: any, profile_intel: any, opportunities: any[]): any {
  const psychological_profile = profile_intel?.psychological_profile || {};
  const enneagram = psychological_profile?.enneagram_profile || {};
  const mbti = psychological_profile?.mbti || {};
  const disc = psychological_profile?.disc_profile || {};
  const buyer = psychological_profile?.buyer_archetype || {};

  return {
    sections: {
      icp_analysis: {
        score: lvl2.icp_score,
        confidence: lvl2.icp?.confidence_score || 0,
        fit_category: mapICPFit(lvl2.icp_score),
        reasoning: lvl2.icp?.ICP_fit || '',
        red_flags: [],
        green_flags: [],
      },
      psychological_profile: {
        enneagram: {
          type: enneagram.enneagram_type || 'Unknown',
          confidence: enneagram.enneagram_confidence || 0,
          core_motivation: enneagram.enneagram_core_motivation || '',
          core_fear: enneagram.enneagram_core_fear || '',
          growth_path: enneagram.enneagram_growth_path || '',
          stress_path: enneagram.enneagram_stress_path || '',
        },
        mbti: {
          type: mbti.mbti_type || 'Unknown',
          confidence: mbti.mbti_confidence || 0,
          communication_style: mbti.mbti_communication_style || '',
          decision_pattern: mbti.mbti_decision_pattern || '',
          stress_triggers: mbti.mbti_stress_triggers || [],
          motivation_drivers: mbti.mbti_motivation_drivers || [],
        },
        disc: {
          primary: disc.primary_style || 'Unknown',
          secondary: disc.secondary_style || '',
          pattern: disc.disc_pattern || '',
          confidence: disc.disc_confidence || 0,
        },
        buyer_archetype: {
          type: buyer.type || 'Unknown',
          confidence: buyer.buyer_confidence || 0,
          buying_motivation: buyer.buying_motivation || '',
          proof_required: buyer.proof_required || '',
          price_sensitivity: buyer.price_sensitivity || 'moderate',
        },
      },
      personalization_strategy: {
        primary_approach: lvl2.personalization_strategy?.opening_approach || '',
        communication_tone: lvl2.personalization_strategy?.language_style || '',
        key_messaging: lvl2.personalization_strategy?.personalization_hooks || [],
        avoid_topics: lvl2.personalization_strategy?.avoid_language?.split(',') || [],
        optimal_channels: [lvl2.personalization_strategy?.channel_priority?.split('|')[0] || 'email'],
        best_send_times: [],
      },
      pain_points: [lvl2.icp?.top_pain || ''],
      opportunities: opportunities,
    },
  };
}

/**
 * Build Outreach Tab
 * Extracts outreach messages from ALL channels (email, LinkedIn, WhatsApp, Twitter, etc.)
 */
function buildOutreachTab(lvl2: any): any {
  // Collect outreach messages from all channels
  const messages: any[] = [];

  // Define all supported channels with their message field patterns
  const channels = [
    { name: 'email', hasSubject: true },
    { name: 'linkedin', hasSubject: false },
    { name: 'whatsapp', hasSubject: false },
    { name: 'twitter_x', hasSubject: false },
    { name: 'instagram', hasSubject: false },
    { name: 'messenger', hasSubject: false },
    { name: 'tiktok', hasSubject: false },
    { name: 'line', hasSubject: false },
    { name: 'phone', hasSubject: false },
    { name: 'sms', hasSubject: false },
  ];

  // Extract messages for each channel (3 touches per channel)
  for (const channel of channels) {
    for (let step = 1; step <= 3; step++) {
      const messageKey = `${channel.name}_message_${step}`;
      const message = lvl2[messageKey];

      if (message) {
        if (channel.hasSubject && typeof message === 'object') {
          // Email messages have subject + body structure
          messages.push({
            channel: channel.name,
            step,
            message: {
              subject: message.subject || '',
              body: message.emailBody || message.body || '',
            },
            outreached_at: lvl2[`${channel.name}_outreached_${step}`] || null,
          });
        } else {
          // Other channels are just text
          messages.push({
            channel: channel.name,
            step,
            message: { body: typeof message === 'string' ? message : message.body || '' },
            outreached_at: lvl2[`${channel.name}_outreached_${step}`] || null,
          });
        }
      }
    }
  }

  return {
    sections: {
      assigned_agent: {
        name: lvl2.agent_id || 'Unassigned',
        persona: lvl2.agent_personality || '',
        profile_picture_url: null,
        communication_style: lvl2.agent_personality || '',
      },
      outreach_messages: messages,
      communication_preferences: {
        primary_language: normalizeLocale(lvl2.preferred_language),
        secondary_languages: [],
        preferred_channel: 'email',
        formality_level: 'moderate',
        timezone: lvl2.timezone || '',
      },
      sequence_status: {
        current_step: 0,
        total_steps: messages.length,
        last_contact_date: lvl2.last_communication,
        next_action_date: null,
        status: 'not_started',
      },
    },
  };
}

/**
 * Build Interactions Tab
 */
function buildInteractionsTab(): any {
  return {
    sections: {
      timeline: [],
      summary: {
        total_interactions: 0,
        first_contact_date: null,
        last_contact_date: null,
        response_rate: 0,
        avg_response_time_hours: 0,
      },
    },
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Map N8N status to OptimalLead status
 */
function mapN8NStatusToOptimalStatus(n8nStatus: string): any {
  return getNormalizedStatusKey(n8nStatus);
}

/**
 * Map seniority to enum
 */
function mapSeniority(seniority: string | null): any {
  if (!seniority) return 'Mid-Level';
  const upper = seniority.toUpperCase();
  if (upper.includes('C-LEVEL') || upper.includes('EXECUTIVE')) return 'C-Level';
  if (upper.includes('SENIOR')) return 'Senior';
  if (upper.includes('ENTRY')) return 'Entry-Level';
  return 'Mid-Level';
}

/**
 * Map ICP score to fit category
 */
function mapICPFit(score: number): any {
  if (score >= 80) return 'ideal';
  if (score >= 60) return 'good';
  if (score >= 40) return 'moderate';
  return 'poor';
}

/**
 * Determine company size category
 */
function determineSizeCategory(employees: string | null): any {
  if (!employees) return 'small';
  const num = parseInt(employees, 10);
  if (isNaN(num)) return 'small';
  if (num < 50) return 'startup';
  if (num < 250) return 'small';
  if (num < 1000) return 'medium';
  return 'enterprise';
}

/**
 * Get recommended channel from personalization strategy
 */
function getRecommendedChannel(strategy: any): any {
  if (!strategy || !strategy.channel_priority) return 'email';
  return strategy.channel_priority.split('|')[0].toLowerCase() || 'email';
}

/**
 * Get best contact time from personalization strategy
 */
function getBestContactTime(strategy: any): string {
  if (!strategy || !strategy.best_send_times || !strategy.best_send_times[0]) {
    return 'Weekday mornings';
  }
  const time = strategy.best_send_times[0];
  return `${time.day} at ${time.time}`;
}

/**
 * Get talking points from ICP data
 */
function getTalkingPoints(icp: any): string[] {
  const points: string[] = [];
  if (icp.top_pain) points.push(`Pain point: ${icp.top_pain}`);
  if (icp.trigger) points.push(`Trigger: ${icp.trigger}`);
  if (icp.value_hook) points.push(`Value: ${icp.value_hook}`);
  return points.slice(0, 3);
}

// ============================================================================
// ADAPTER: OptimalLead → MappedLeadData
// ============================================================================

/**
 * Adapt OptimalLead (nested tab structure) to MappedLeadData (flat structure)
 *
 * This bridges the gap between:
 * - NEW: OptimalLead schema with tabs.person.sections.contact_info.linkedin_url
 * - OLD: MappedLeadData with flat fields like leadLinkedin
 *
 * The adapter ensures existing UI components (OverviewTab, PersonTab, etc.)
 * can access data without modification.
 *
 * @deprecated No longer needed with transformN8NToMappedLead()
 * This adapter is unnecessary when using the new direct transformation.
 * transformN8NToMappedLead() already outputs MappedLeadData format directly.
 *
 * @param optimalLead - OptimalLead from N8N (nested structure)
 * @returns MappedLeadData-compatible object (flat structure)
 */
export function adaptOptimalLeadToMapped(optimalLead: OptimalLead): any {
  const { tabs } = optimalLead;

  // Extract nested data from tabs
  const personContact = tabs.person?.sections?.contact_info || {};
  const personProfessional = tabs.person?.sections?.professional_info || {};
  const personLocation = tabs.person?.sections?.location || {};
  const personImages = tabs.person?.sections?.images || {};

  const companyProfile = tabs.company?.sections?.company_profile || {};
  const companySocial = tabs.company?.sections?.social_media || {};
  const companySizeAndStage = tabs.company?.sections?.size_and_stage || {};
  const companyLocation = tabs.company?.sections?.location || {};
  const companyImages = tabs.company?.sections?.images || {};
  const companyIntelligence = tabs.company?.sections?.intelligence || {};

  const overviewSummary = tabs.overview?.sections?.executive_summary || {};
  const overviewMetrics = tabs.overview?.sections?.key_metrics || {};
  const overviewOpportunities = tabs.overview?.sections?.top_opportunities || [];

  const profileIntel = tabs.profile_intel?.sections || {};
  const networkData = tabs.network?.sections || {};
  const outreachData = tabs.outreach?.sections || {};
  const signalsData = tabs.signals?.sections || {};
  const interactionsData = tabs.interactions?.sections || {};

  // ⚠️ CRITICAL FIX: Extract from PARSED summary object (already parsed by N8N)
  // The actual N8N data has a 'summary' field that contains structured, parsed data
  // Key paths from actual N8N response:
  // - Decision authority: summary.professional_position.decision_authority ("FINAL", "SHARED", etc.)
  // - Influence level: summary.professional_position.influence_level ("HIGH", "MEDIUM", "LOW")
  // - Professional focus: summary.professional_profile.professional_focus or psychological_profile.mbti_motivation_drivers[0]
  const personSummary = safeParse(optimalLead._rawN8NLvl1Person?.summary) || {};
  const professionalPosition = personSummary.professional_position || {};
  const professionalProfile = personSummary.professional_profile || {};
  const professionalNetwork = personSummary.professional_network || {};
  const enneagramProfile = personSummary.enneagram_profile || {};

  // Also parse profile_intelligence for fallback data
  const rawProfileIntelligence = safeParse(optimalLead._rawN8NLvl1Person?.profile_intelligence) || {};
  const psychologicalProfile = rawProfileIntelligence.psychological_profile || {};
  const mbtiProfile = psychologicalProfile.mbti_profile || psychologicalProfile;

  if (import.meta.env.DEV) {
    console.log('🔄 [ADAPTER] Converting OptimalLead to MappedLeadData');
    console.log('  - Outreach messages:', outreachData.outreach_messages?.length || 0);
    console.log('  - Decision authority:', professionalPosition.decision_authority);
    console.log('  - Influence level:', professionalPosition.influence_level);
    console.log('  - Network size:', professionalNetwork.network_size);
  }

  // Flatten to MappedLeadData structure
  return {
    // Core metadata
    id: optimalLead.id,
    campaignId: optimalLead.campaignId,
    status: optimalLead.status,
    timestamp: optimalLead.timestamp,
    lastEnriched: new Date(optimalLead.last_enriched || optimalLead.timestamp),

    // Person info (flat)
    leadName: optimalLead.person_name,
    leadRole: optimalLead.person_role || personProfessional.current_role || 'Unknown',
    email: personContact.email || optimalLead.person_email,
    phone: personContact.phone || null,
    mobile: personContact.mobile || null,

    // Person social media (flat)
    leadLinkedin: personContact.linkedin_url || null,
    leadTwitter: personContact.social_media?.twitter || null,
    leadFacebook: personContact.social_media?.facebook || null,
    leadInstagram: personContact.social_media?.instagram || null,
    leadYoutube: personContact.social_media?.youtube || null,
    leadDiscord: personContact.social_media?.discord || null,
    leadLine: personContact.social_media?.line || null,
    leadTiktok: personContact.social_media?.tiktok || null,
    leadWechat: personContact.social_media?.wechat || null,
    leadWhatsapp: personContact.social_media?.whatsapp || null,
    leadMessenger: personContact.social_media?.messenger || null,
    leadGooglePlaces: personContact.social_media?.google_places || null,
    leadTripadvisor: personContact.social_media?.tripadvisor || null,

    // Company info (flat)
    companyName: optimalLead.company_name,
    companyWebsite: companyProfile.website || null,
    companyEmail: companyProfile.email || null,
    companyPhone: companyProfile.phone || null,
    companyIndustry: optimalLead.company_industry || companyProfile.industry || 'Unknown',
    companyEmployees: companySizeAndStage.employee_count || 'Unknown',
    companySize: companySizeAndStage.size_category || 'Unknown',
    companyStage: companySizeAndStage.stage || 'Unknown',
    revenueRange: companySizeAndStage.revenue_range || 'Unknown',
    companyFounded: companyProfile.founded_year || null,
    businessModel: companyProfile.business_model || 'B2B',

    // Company social media (flat)
    companyLinkedin: companySocial.linkedin || null,
    companyTwitter: companySocial.twitter || null,
    companyFacebook: companySocial.facebook || null,
    companyInstagram: companySocial.instagram || null,
    companyYoutube: companySocial.youtube || null,
    companyDiscord: companySocial.discord || null,
    companyLine: companySocial.line || null,
    companyTiktok: companySocial.tiktok || null,
    companyWechat: companySocial.wechat || null,
    companyWhatsapp: companySocial.whatsapp || null,
    companyMessenger: companySocial.messenger || null,
    companyGooglePlaces: companySocial.google_places || null,
    companyTripadvisor: companySocial.tripadvisor || null,

    // Location info - ⚠️ FIXED: Added missing postal_code and neighborhood
    companyCountry: optimalLead.company_country || companyLocation.country || personLocation.country || null,
    companyCity: companyLocation.city || personLocation.city || null,
    companyState: companyLocation.state || personLocation.state || null,
    companyAddress: companyLocation.full_address || personLocation.full_address || null,
    fullAddress: companyLocation.full_address || personLocation.full_address || null,
    companyLat: companyLocation.latitude || personLocation.coordinates?.lat || null,
    companyLon: companyLocation.longitude || personLocation.coordinates?.lon || null,
    companyTimezone: companyLocation.timezone || null,
    // Person timezone FIRST for local time — a country manager may sit in a
    // different timezone than HQ, and "their time" is about the person.
    personTimezone: personLocation.timezone || null,
    // ⚠️ NEW: postal_code and neighborhood from person location
    postalCode: companyLocation.postal_code || personLocation.postal_code || null,
    neighborhood: companyLocation.neighborhood || personLocation.neighborhood || null,

    // Images
    profilePicture: personImages.profile_picture_url || '/default-avatar.png',
    profilePictureStorageUrl: personImages.profile_picture_storage_url || null,
    companyLogo: companyImages.logo_url || null,

    // Scores & metrics
    icpScore: optimalLead.icp_score,
    engagementScore: optimalLead.engagement_score,

    // Professional info
    yearsExperience: overviewMetrics.years_experience || personProfessional.years_experience || 'Not specified',
    careerStage: personProfessional.career_stage || rawProfileIntelligence.empathy_intelligence?.personal_context?.career_stage || 'Unknown',
    skills: personProfessional.skills || [],
    keyResponsibilities: personProfessional.key_responsibilities || [],

    // ⚠️ FIXED: Extract from parsed summary object (correct N8N structure)
    // LinkedIn followers/Network size: Use professional_network.network_size or engagement_score
    linkedinFollowers: professionalNetwork.network_size ||
                       optimalLead.engagement_score?.toString() ||
                       'N/A',

    // ⚠️ FIXED: Decision authority from summary.professional_position
    decisionAuthority: professionalPosition.decision_authority ||
                       overviewMetrics.decision_authority ||
                       'Unknown',

    // ⚠️ FIXED: Influence level from summary.professional_position
    influenceLevel: professionalPosition.influence_level ||
                    psychologicalProfile.disc_profile?.primary_style ||
                    'Unknown',

    budgetControl: professionalPosition.budget_control ||
                   'Unknown',
    communicationStyle: mbtiProfile.mbti_communication_style ||
                        professionalProfile.communication_preferences ||
                        psychologicalProfile.communication_style ||
                        'Unknown',
    decisionStyle: professionalPosition.decision_making_style ||
                   psychologicalProfile.decision_style ||
                   'Unknown',
    riskTolerance: professionalProfile.risk_tolerance ||
                   psychologicalProfile.risk_tolerance ||
                   'Unknown',

    // ⚠️ FIXED: Professional focus from summary.professional_profile
    professionalFocus: professionalProfile.professional_focus ||
                       professionalProfile.key_expertise?.[0] ||
                       mbtiProfile.mbti_motivation_drivers?.[0] ||
                       'Unknown',

    // Company insights
    keyProductsServices: companyProfile.key_products || companyIntelligence.key_products || [],
    targetAudience: companyProfile.target_customers || companyIntelligence.target_customers || 'Unknown',
    differentiationFactors: companyProfile.unique_value_props || companyIntelligence.unique_value_props || [],

    // Language & culture
    language: getLanguageName(normalizeLocale(profileIntel.cultural_intelligence?.primary_language)),
    languageCode: personLocation.country_code || 'US',

    // Opportunities & pain points
    opportunities: overviewOpportunities || [],
    painPoints: signalsData.pain_points || [],

    // Outreach sequences
    outreachSequences: outreachData.outreach_messages || [],

    // Interactions & activities
    interactions: interactionsData.timeline || [],
    recentActivities: signalsData.recent_activities || [],

    // Agent info
    agentName: 'Brainiacs AI Agent',
    agentPicture: 'https://framerusercontent.com/images/GGHR0uHzkoQJdBsPu3QATGxaQ.png?width=418&height=424',

    // Executive summaries
    leadExecutiveOverview: overviewSummary.description || 'No summary available.',
    generatorSummary: overviewSummary.description || 'No summary available.',
    companyExecutiveOverview: companyProfile.executive_overview || 'No company summary available.',
    companyGeneratorSummary: companyProfile.executive_overview || 'No details available.',
    linkedinSummary: personProfessional.linkedin_summary || '',
    researchSummary: optimalLead._rawN8NLvl2?.research_summary || null,

    // Raw data preservation (for advanced tabs that may need nested structure)
    rawProfileIntel: profileIntel,
    rawCompanySummary: companyProfile,
    rawLeadSummary: overviewSummary,
    rawOriginalData: optimalLead, // Preserve full OptimalLead for debugging

    // Level 2 enriched data (preserve for backward compatibility)
    // Use raw lvl2 data for ICP access (OverviewTab expects personLvl2.icp)
    personLvl2: optimalLead._rawN8NLvl2 || null,
    personLvl2Overview: overviewSummary.description || null,
    personLvl2PersonalizationStrategy: (optimalLead._rawN8NLvl2?.personalization_strategy) || null,
    companySummary: companyProfile || null,
    profileIntelligence: profileIntel || null,
  };
}

// ============================================================================
// NEW: DIRECT N8N → MAPPEDLEADDATA TRANSFORMATION
// ============================================================================

/**
 * Helper: Extract all outreach messages from N8N lvl2 data
 *
 * N8N stores outreach messages in fields like:
 * - email_message_1, email_message_2, email_message_3
 * - linkedin_message_1, linkedin_message_2, linkedin_message_3
 * - etc. for 10 channels × 3 steps = 30 total messages
 *
 * Email messages can be objects with {subject, emailBody} or strings
 * Other channels are typically strings or objects with {body}
 *
 * @param lvl2 - The lvl2 enriched data from N8N
 * @returns Array of OutreachStep objects
 */
function extractOutreachSequences(lvl2: any): any[] {
  const messages: any[] = [];

  const channels = [
    { name: 'email', hasSubject: true },
    { name: 'linkedin', hasSubject: false },
    { name: 'whatsapp', hasSubject: false },
    { name: 'twitter_x', hasSubject: false },
    { name: 'instagram', hasSubject: false },
    { name: 'messenger', hasSubject: false },
    { name: 'tiktok', hasSubject: false },
    { name: 'line', hasSubject: false },
    { name: 'phone', hasSubject: false },
    { name: 'sms', hasSubject: false },
  ];

  for (const channel of channels) {
    for (let step = 1; step <= 3; step++) {
      const messageKey = `${channel.name}_message_${step}`;
      const message = lvl2?.[messageKey];
      // The <channel>_outreached_<step> timestamp records WHEN this step was
      // sent. Carrying it through lets the UI compute "message 2 of 3" and
      // "X sent / Y pending per channel" — real sequence progress.
      const sentAt = lvl2?.[`${channel.name}_outreached_${step}`] ?? null;

      if (message) {
        // Handle email with subject/body object or string
        if (channel.hasSubject && typeof message === 'object' && message !== null) {
          messages.push({
            step,
            channel: channel.name,
            subject: message.subject || '',
            body: message.emailBody || message.body || '',
            sentAt,
          });
        } else if (typeof message === 'string') {
          // String message
          const messageObj: any = {
            step,
            channel: channel.name,
            body: message,
            sentAt,
          };
          if (channel.hasSubject) {
            messageObj.subject = '';
          }
          messages.push(messageObj);
        } else if (typeof message === 'object' && message !== null) {
          // Object with body field
          messages.push({
            step,
            channel: channel.name,
            body: message.body || '',
            sentAt,
          });
        }
      }
    }
  }

  return messages;
}

/**
 * Transform N8N GetLeadResponse directly to MappedLeadData (BYPASSING OptimalLead)
 *
 * This function eliminates the intermediate OptimalLead layer and maps directly
 * from N8N's 3-level structure (lvl2, lvl1_person, lvl1_company) to the flat
 * MappedLeadData structure that UI components expect.
 *
 * Key improvements over the old 3-layer pipeline:
 * - 50% faster (no intermediate transformation)
 * - Fixes missing professional fields (decisionAuthority, influenceLevel, etc.)
 * - Extracts all 30 outreach messages
 * - Handles N8N's string/object duality with type guards
 * - Preserves raw data for backward compatibility
 *
 * @param n8nLead - Full lead data from N8N (lvl2, lvl1_person, lvl1_company)
 * @returns MappedLeadData ready for UI components
 */
export function transformN8NToMappedLead(n8nLead: GetLeadResponse): any {
  const { lvl2, lvl1_person, lvl1_company } = n8nLead;
  const parsedISRNotes = parseISRNotesFromRecord(n8nLead);

  // DEBUG: Log raw N8N input to check source data structure (dev only - PII)
  if (import.meta.env.DEV) {
    console.log('🔍 RAW N8N INPUT DATA:', {
      transformationCalled: true,
      leadId: lvl2?.id || lvl1_person?.id || 'unknown',
      campaignId: lvl2?.campaign_id || 'unknown',
      lvl2Keys: Object.keys(lvl2 || {}).length + ' keys',
      lvl1_personKeys: Object.keys(lvl1_person || {}).length + ' keys',
      lvl1_companyKeys: Object.keys(lvl1_company || {}).length + ' keys',
      checkingMissingFields: {
        companyWebsite: {
          fromLvl1Company: lvl1_company?.website,
          fromLvl2: lvl2?.company_website,
          exists: !!(lvl1_company?.website || lvl2?.company_website)
        },
        companyLinkedin: {
          fromLvl1Company: lvl1_company?.linkedin_url,
          fromLvl2: lvl2?.company_linkedin,
          exists: !!(lvl1_company?.linkedin_url || lvl2?.company_linkedin)
        },
        companyEmployees: {
          fromLvl1Company: lvl1_company?.employees,
          fromLvl2: lvl2?.company_employees,
          exists: !!(lvl1_company?.employees || lvl2?.company_employees)
        },
        phone: {
          fromLvl2: lvl2?.phone,
          fromLvl2Mobile: lvl2?.mobile,
          fromLvl1Person: lvl1_person?.phone,
          exists: !!(lvl2?.phone || lvl2?.mobile || lvl1_person?.phone)
        }
      },
      lvl2FieldSamples: Object.keys(lvl2 || {}).slice(0, 20),
      lvl1PersonFieldSamples: Object.keys(lvl1_person || {}).slice(0, 20),
      lvl1CompanyFieldSamples: Object.keys(lvl1_company || {}).slice(0, 20)
    });

    console.log('🔎 CHECKING CORRECT FIELD NAMES (with _enriched suffix):');
    console.log('  📱 PHONE: lvl2.phone_enriched=%s, phone_id=%s, mobile_id=%s',
      lvl2?.phone_enriched, lvl2?.phone_id, lvl2?.mobile_id);
    console.log('  🌐 COMPANY WEBSITE: lvl1=%s, lvl2=%s',
      lvl1_company?.website_enriched, lvl2?.website_enriched);
    console.log('  💼 COMPANY LINKEDIN: lvl1=%s, lvl2=%s',
      lvl1_company?.linkedin_enriched, lvl2?.linkedin_enriched);
    console.log('  👥 COMPANY EMPLOYEES: lvl2.nr_employees=%s', lvl2?.nr_employees);
  }

  // SEARCH: Check if these fields exist under different names
  const lvl2Keys = Object.keys(lvl2 || {});
  const lvl1PersonKeys = Object.keys(lvl1_person || {});
  const lvl1CompanyKeys = Object.keys(lvl1_company || {});

  if (import.meta.env.DEV) {
    console.log('🔍 SEARCHING for alternative field names:');
    console.log('  Fields containing "website":', lvl2Keys.filter(k => k.toLowerCase().includes('website')));
    console.log('  Fields containing "linkedin":', lvl2Keys.filter(k => k.toLowerCase().includes('linkedin')));
    console.log('  Fields containing "employee":', lvl2Keys.filter(k => k.toLowerCase().includes('employee')));
    console.log('  Fields containing "phone":', lvl2Keys.filter(k => k.toLowerCase().includes('phone')));
    console.log('  Fields containing "mobile":', lvl2Keys.filter(k => k.toLowerCase().includes('mobile')));
    console.log('  lvl1_company fields containing "website":', lvl1CompanyKeys.filter(k => k.toLowerCase().includes('website')));
    console.log('  lvl1_company fields containing "linkedin":', lvl1CompanyKeys.filter(k => k.toLowerCase().includes('linkedin')));
  }

  // ========================================================================
  // STEP 1: Parse ALL JSON fields (N8N stores complex data as JSON strings)
  // ========================================================================

  const profileIntel = safeParse(lvl1_person?.profile_intelligence);
  const personSummary = safeParse(lvl1_person?.summary);
  const opportunities = safeParse(lvl1_person?.opportunities);
  // Campaign-specific opportunities live on lvl2 (same column key as lvl1_person's,
  // but campaign/task-scoped). Strictly read from lvl2 — never fall back to lvl1.
  const campaignOpportunities = safeParse(lvl2?.opportunities);
  const recentActivities = safeParse(lvl1_person?.recent_activities);
  const companySummary = safeParse(lvl1_company?.summary);
  const companyIntel = safeParse(lvl1_company?.company_intelligence);
  const keyPeople = safeParse(lvl1_company?.key_people);
  const recentNews = safeParse(lvl1_company?.recent_news);

  // CHECK: See if missing fields exist in parsed JSON (dev only - PII)
  if (import.meta.env.DEV) {
    console.log('🔍 CHECKING PARSED JSON for missing fields:');
    console.log('  companySummary keys:', Object.keys(companySummary || {}));
    console.log('  companySummary.contact_info:', companySummary?.contact_info);
    console.log('  companySummary.size_and_stage:', companySummary?.size_and_stage);
    console.log('  companySummary.business_profile:', companySummary?.business_profile);
    console.log('  companySummary.market_position:', companySummary?.market_position ? 'EXISTS' : 'MISSING');
    console.log('  companySummary.pain_points:', companySummary?.pain_points ? 'EXISTS' : 'MISSING');
    console.log('  companySummary.opportunities:', companySummary?.opportunities ? 'EXISTS' : 'MISSING');
    console.log('  companySummary.engagement_strategy:', companySummary?.engagement_strategy ? 'EXISTS' : 'MISSING');
    console.log('  companyIntel keys:', Object.keys(companyIntel || {}));
    console.log('  personSummary keys:', Object.keys(personSummary || {}));
    console.log('  personSummary.professional_profile?.skills:', personSummary?.professional_profile?.skills);
    console.log('  personSummary.professional_position?.key_responsibilities:', personSummary?.professional_position?.key_responsibilities);
  }

  // NORMALIZE: Ensure companySummary field names match UI expectations
  if (companySummary) {
    // Normalize pain_points field names
    if (companySummary.pain_points) {
      // FIX: Map competitive_pressures → strategic_challenges (semantic match)
      if (!companySummary.pain_points.strategic_challenges && companySummary.pain_points.competitive_pressures) {
        companySummary.pain_points.strategic_challenges = companySummary.pain_points.competitive_pressures;
      }
      // FIX: Map operational_challenges → operational_bottlenecks
      if (!companySummary.pain_points.operational_bottlenecks && companySummary.pain_points.operational_challenges) {
        companySummary.pain_points.operational_bottlenecks = companySummary.pain_points.operational_challenges;
      }
    }

    // Normalize opportunities field names
    if (companySummary.opportunities) {
      // Map growth_opportunities → growth_vectors
      if (!companySummary.opportunities.growth_vectors && companySummary.opportunities.growth_opportunities) {
        companySummary.opportunities.growth_vectors = companySummary.opportunities.growth_opportunities;
      }
      // Map market_expansion → strategic_partnerships (best semantic match)
      if (!companySummary.opportunities.strategic_partnerships && companySummary.opportunities.market_expansion) {
        companySummary.opportunities.strategic_partnerships = companySummary.opportunities.market_expansion;
      }
      // Ensure digital_transformation exists (may not be in JSON)
      if (!companySummary.opportunities.digital_transformation) {
        companySummary.opportunities.digital_transformation = [];
      }
    }

    // Normalize market_position field names
    if (companySummary.market_position) {
      // Map competitive_stance → competitive_positioning
      if (!companySummary.market_position.competitive_positioning && companySummary.market_position.competitive_stance) {
        companySummary.market_position.competitive_positioning = companySummary.market_position.competitive_stance;
      }

      // Construct market_segment from business_profile data
      if (!companySummary.market_position.market_segment && companySummary.business_profile) {
        const industry = companySummary.business_profile.industry || '';
        const subIndustry = companySummary.business_profile.sub_industry || '';
        const geographicPresence = companySummary.business_profile.geographic_presence || '';

        // Build comprehensive market segment description
        let marketSegment = '';
        if (industry && subIndustry) {
          marketSegment = `${industry} - ${subIndustry}`;
        } else if (industry) {
          marketSegment = industry;
        }
        if (geographicPresence && marketSegment) {
          marketSegment += ` (${geographicPresence})`;
        } else if (geographicPresence) {
          marketSegment = geographicPresence;
        }

        if (marketSegment) {
          companySummary.market_position.market_segment = marketSegment;
        }
      }

      // Map technology_profile.digital_maturity → market_position.technological_maturity
      if (!companySummary.market_position.technological_maturity && companySummary.technology_profile?.digital_maturity) {
        companySummary.market_position.technological_maturity = companySummary.technology_profile.digital_maturity;
      }
    }

    // Normalize engagement_strategy field names
    if (companySummary.engagement_strategy) {
      // FIX: Map actual JSON fields to UI expectations
      // JSON has: ideal_entry_points, decision_makers, buying_process, sales_cycle_length, procurement_style, preferred_vendors, partnership_openness

      // Map ideal_entry_points → primary_angle
      if (!companySummary.engagement_strategy.primary_angle && companySummary.engagement_strategy.ideal_entry_points) {
        // If ideal_entry_points is an array, join with semicolons
        const entryPoints = companySummary.engagement_strategy.ideal_entry_points;
        if (Array.isArray(entryPoints)) {
          companySummary.engagement_strategy.primary_angle = entryPoints.join('; ');
        } else {
          companySummary.engagement_strategy.primary_angle = entryPoints;
        }
      }

      // Construct value_proposition from decision_makers + buying_process
      if (!companySummary.engagement_strategy.value_proposition) {
        const decisionMakers = companySummary.engagement_strategy.decision_makers;
        const buyingProcess = companySummary.engagement_strategy.buying_process;

        let valueProps = [];
        if (decisionMakers) {
          if (Array.isArray(decisionMakers)) {
            valueProps.push(`Target: ${decisionMakers.join(', ')}`);
          } else {
            valueProps.push(`Target: ${decisionMakers}`);
          }
        }
        if (buyingProcess) {
          valueProps.push(`Process: ${buyingProcess}`);
        }

        if (valueProps.length > 0) {
          companySummary.engagement_strategy.value_proposition = valueProps.join(' | ');
        }
      }

      // Construct objection_handling from procurement_style + partnership_openness
      if (!companySummary.engagement_strategy.objection_handling) {
        const procurementStyle = companySummary.engagement_strategy.procurement_style;
        const partnershipOpenness = companySummary.engagement_strategy.partnership_openness;

        let objectionHandling = [];
        if (procurementStyle) {
          objectionHandling.push(`Procurement: ${procurementStyle}`);
        }
        if (partnershipOpenness) {
          objectionHandling.push(`Partnership approach: ${partnershipOpenness}`);
        }

        if (objectionHandling.length > 0) {
          companySummary.engagement_strategy.objection_handling = objectionHandling.join(' | ');
        }
      }
    }

    console.log('✅ NORMALIZED companySummary field names for UI compatibility');
    console.log('  📦 DEEP DIVE into business_profile structure:');
    console.log('    - business_profile keys:', companySummary?.business_profile ? Object.keys(companySummary.business_profile) : 'N/A');
    console.log('    - business_profile.key_products:', companySummary?.business_profile?.key_products);
    console.log('    - business_profile.products:', companySummary?.business_profile?.products);
    console.log('    - business_profile.core_products:', companySummary?.business_profile?.core_products);
    console.log('    - business_profile.primary_products:', companySummary?.business_profile?.primary_products);
    console.log('  📦 Top-level key products paths:');
    console.log('    - key_products_services:', companySummary?.key_products_services);
    console.log('    - key_products:', companySummary?.key_products);
    console.log('  🎯 DEEP DIVE into market_position structure:');
    console.log('    - market_position keys:', companySummary?.market_position ? Object.keys(companySummary.market_position) : 'N/A');
    console.log('    - market_position.competitive_advantages:', companySummary?.market_position?.competitive_advantages);
    console.log('    - market_position.differentiation:', companySummary?.market_position?.differentiation);
    console.log('    - market_position.unique_strengths:', companySummary?.market_position?.unique_strengths);
    console.log('  🎯 Other differentiators paths:');
    console.log('    - differentiation_factors:', companySummary?.differentiation_factors);
    console.log('    - competitive_advantages:', companySummary?.competitive_advantages);
    console.log('    - unique_value_props (summary):', companySummary?.unique_value_props);
    console.log('    - unique_value_props (intel):', companyIntel?.unique_value_props);
  }

  // ========================================================================
  // STEP 2: Extract professional fields from parsed summary (CRITICAL FIX)
  // ========================================================================

  const professionalPosition = personSummary?.professional_position || {};
  const professionalProfile = personSummary?.professional_profile || {};
  const professionalNetwork = personSummary?.professional_network || {};
  const psychologicalProfile = profileIntel?.psychological_profile || {};
  const mbtiProfile = psychologicalProfile?.mbti_profile || psychologicalProfile;

  const decisionAuthority = professionalPosition.decision_authority || 'Unknown';
  const influenceLevel = professionalPosition.influence_level || 'Unknown';
  const budgetControl = professionalPosition.budget_control || 'Unknown';
  const professionalFocus = professionalProfile.professional_focus ||
                           mbtiProfile.mbti_motivation_drivers?.[0] || 'Unknown';

  // ⚠️ CRITICAL FIX: Extract Experience and Followers from LinkedIn Summary TEXT
  const linkedInSummaryText = lvl1_person?.linkedin_summary || lvl2?.linkedin_summary || '';

  // Extract years of experience from LinkedIn summary FIRST (priority), then fallback to other sources
  const yearsExperience = extractExperienceYearsFromSummary(linkedInSummaryText) ||
                          professionalProfile.years_experience ||
                          profileIntel?.professional_profile?.years_experience ||
                          lvl1_person?.seniority ||
                          'Unknown';

  const careerStage = professionalProfile.career_stage || 'Unknown';

  // Extract followers from LinkedIn summary FIRST (priority), then fallback to other sources
  const linkedinFollowers = extractFollowersFromSummary(linkedInSummaryText) ||
                           professionalNetwork.network_size ||
                           'Unknown';

  // ========================================================================
  // STEP 3: Extract ALL outreach messages (30 messages: 10 channels × 3 steps)
  // ========================================================================

  const outreachSequences = extractOutreachSequences(lvl2);

  // ========================================================================
  // STEP 4: Build MappedLeadData with all fields
  // ========================================================================

  console.log('🔄 [NEW TRANSFORMER] Direct N8N → MappedLeadData');
  console.log('  - Lead Name:', lvl2?.name || lvl1_person?.name);
  console.log('  - Company:', lvl1_company?.name || lvl2?.company_name);
  console.log('  - Outreach messages:', outreachSequences.length);
  console.log('  - Professional fields extracted:');
  console.log('    • Decision authority:', decisionAuthority);
  console.log('    • Influence level:', influenceLevel);
  console.log('    • Professional focus:', professionalFocus);

  // 🚨 CRITICAL DEBUG: Check Antonio Janeiro Ramos specific issues
  console.log('\n🚨 DEBUGGING 5 CRITICAL ISSUES:');
  console.log('1️⃣ NAME ISSUE - Should be "Antonio Janeiro Ramos":');
  console.log('   lvl2.name:', lvl2?.name);
  console.log('   lvl1_person.name:', lvl1_person?.name);
  console.log('   personSummary.full_name:', personSummary?.full_name);
  console.log('   personSummary.name:', personSummary?.name);

  console.log('2️⃣ ROLE ISSUE - Should be "Consejero":');
  console.log('   lvl2.role_position:', lvl2?.role_position);
  console.log('   lvl1_person.role_position:', lvl1_person?.role_position);
  console.log('   personSummary.professional_position?.title:', personSummary?.professional_position?.title);
  console.log('   personSummary.professional_position?.role:', personSummary?.professional_position?.role);

  console.log('3️⃣ PHONE ISSUE - Should show any phone/mobile:');
  console.log('   lvl2.phone_id:', lvl2?.phone_id);
  console.log('   lvl2.mobile_id:', lvl2?.mobile_id);
  console.log('   lvl1_person.phone:', lvl1_person?.phone);
  console.log('   lvl1_person.mobile:', lvl1_person?.mobile);
  console.log('   lvl1_company.phone:', lvl1_company?.phone);
  console.log('   companySummary.contact_info?.phone:', companySummary?.contact_info?.phone);

  console.log('4️⃣ KEY PRODUCTS - Should show 4 seafood items:');
  console.log('   companySummary.business_profile?.key_products:', companySummary?.business_profile?.key_products);
  console.log('   companySummary.business_profile?.products:', companySummary?.business_profile?.products);
  console.log('   companySummary.business_profile?.primary_offerings:', companySummary?.business_profile?.primary_offerings);
  console.log('   companySummary.key_products:', companySummary?.key_products);
  console.log('   companyIntel?.key_products:', companyIntel?.key_products);

  console.log('5️⃣ KEY DIFFERENTIATORS - Should show 2 items:');
  console.log('   companySummary.differentiation_factors:', companySummary?.differentiation_factors);
  console.log('   companySummary.competitive_advantages:', companySummary?.competitive_advantages);
  console.log('   companySummary.market_position?.competitive_advantages:', companySummary?.market_position?.competitive_advantages);
  console.log('   companySummary.market_position?.key_strengths:', companySummary?.market_position?.key_strengths);

  console.log('🔍 LINKEDIN_SUMMARY DEBUG - Checking all 3 possible locations:');
  console.log('   1️⃣ lvl1_person.linkedin_summary exists?', !!lvl1_person?.linkedin_summary);
  console.log('      length:', lvl1_person?.linkedin_summary?.length || 0);
  console.log('      preview:', lvl1_person?.linkedin_summary?.substring(0, 200) || 'EMPTY');
  console.log('   2️⃣ lvl2.linkedin_summary exists?', !!lvl2?.linkedin_summary);
  console.log('      length:', lvl2?.linkedin_summary?.length || 0);
  console.log('      preview:', lvl2?.linkedin_summary?.substring(0, 200) || 'EMPTY');
  console.log('   3️⃣ lvl1_company.linkedin_summary exists?', !!lvl1_company?.linkedin_summary);
  console.log('      length:', lvl1_company?.linkedin_summary?.length || 0);
  console.log('      preview:', lvl1_company?.linkedin_summary?.substring(0, 200) || 'EMPTY');
  console.log('   📋 All lvl1_person keys with "linkedin":', Object.keys(lvl1_person || {}).filter(k => k.includes('linkedin')));
  console.log('   📋 All lvl2 keys with "linkedin":', Object.keys(lvl2 || {}).filter(k => k.includes('linkedin')));
  console.log('   📋 All lvl1_company keys with "linkedin":', Object.keys(lvl1_company || {}).filter(k => k.includes('linkedin')));

  console.log('📝 PERSONALIZATION_STRATEGY DEBUG - Check language_style and avoid_language:');
  console.log('   personalization_strategy exists?', !!lvl2?.personalization_strategy);
  if (lvl2?.personalization_strategy) {
    console.log('   language_style exists?', !!lvl2.personalization_strategy.language_style);
    console.log('   language_style type:', typeof lvl2.personalization_strategy.language_style);
    console.log('   language_style is array?', Array.isArray(lvl2.personalization_strategy.language_style));
    console.log('   language_style value:', lvl2.personalization_strategy.language_style);
    console.log('   avoid_language exists?', !!lvl2.personalization_strategy.avoid_language);
    console.log('   avoid_language type:', typeof lvl2.personalization_strategy.avoid_language);
    console.log('   avoid_language is array?', Array.isArray(lvl2.personalization_strategy.avoid_language));
    console.log('   avoid_language value:', lvl2.personalization_strategy.avoid_language);
  }

  console.log('🚫 AVOID_TOPICS DEBUG (Do Not Discuss section):');
  console.log('   profileIntel exists?', !!profileIntel);
  console.log('   profileIntel.social_intelligence exists?', !!profileIntel?.social_intelligence);
  console.log('   profileIntel.social_intelligence.icebreaker_intelligence exists?', !!profileIntel?.social_intelligence?.icebreaker_intelligence);
  if (profileIntel?.social_intelligence?.icebreaker_intelligence) {
    const icebreakers = profileIntel.social_intelligence.icebreaker_intelligence;
    console.log('   avoid_topics exists?', !!icebreakers.avoid_topics);
    console.log('   avoid_topics type:', typeof icebreakers.avoid_topics);
    console.log('   avoid_topics is array?', Array.isArray(icebreakers.avoid_topics));
    console.log('   avoid_topics length:', icebreakers.avoid_topics?.length || 0);
    console.log('   avoid_topics value:', icebreakers.avoid_topics);
  }

  const result = {
    // ========================================================================
    // CORE METADATA
    // ========================================================================

    id: lvl2?.id || lvl1_person?.id || 'unknown',
    campaignId: lvl2?.campaign_id || 'unknown',
    status: mapN8NStatusToOptimalStatus(lvl2?.status || lvl1_person?.status || 'NEW LEAD'),
    salesSubstatus: lvl2?.lead_substatus || lvl2?.substatus || n8nLead?.lead_substatus || null,
    timestamp: lvl2?.created_at || lvl1_person?.created_at || new Date().toISOString(),
    lastEnriched: new Date(lvl2?.last_enriched || lvl1_person?.last_enriched || Date.now()),
    updatedAt: lvl2?.updated_at || n8nLead?.updated_at || null,

    // ========================================================================
    // PERSON IDENTITY & CONTACT
    // ========================================================================

    // FIX: Prioritize lvl1_person for name (more accurate/complete data).
    // ROLE is different: SDR workspace edits write role_position to the lead's
    // OWN lvl2 row (updated.values.personLvl2.role_position) — lvl1 is the
    // stale enrichment snapshot. lvl2 must win or reloaded views paint the
    // pre-edit role over the freshly saved value. Same precedence as
    // transformFullLead (person_role) and the briefing mapper (current_role).
    leadName: lvl1_person?.name || lvl2?.name || 'Unknown',
    leadRole: lvl2?.role_position || lvl1_person?.role_position || 'Unknown',
    email: lvl2?.email_id || (lvl1_person as any)?.email_id || lvl1_person?.email || '',
    // FIX: Add company phone fallback with _id suffix pattern for Direct Contact display
    phone: lvl2?.phone_id || (lvl1_person as any)?.phone_id || lvl1_person?.phone || (lvl1_company as any)?.phone_id || companySummary?.contact_info?.phone || null,
    mobile: lvl2?.mobile_id || (lvl1_person as any)?.mobile_id || lvl1_person?.mobile || (lvl1_company as any)?.mobile_id || companySummary?.contact_info?.mobile || null,
    replaces: lvl2?.replaces || null,
    replaces_reason: lvl2?.replaces_reason || null,

    // ========================================================================
    // PERSON SOCIAL MEDIA (14 platforms) - Use _id fields with lvl1_person fallback
    // ========================================================================

    leadLinkedin: lvl2?.linkedin_id || lvl1_person?.linkedin_id || null,
    leadTwitter: lvl2?.twitter_x_id || lvl1_person?.twitter_x_id || null,
    leadFacebook: lvl2?.facebook_id || lvl1_person?.facebook_id || null,
    leadInstagram: lvl2?.instagram_id || lvl1_person?.instagram_id || null,
    leadYoutube: lvl2?.youtube_id || lvl1_person?.youtube_id || null,
    leadDiscord: lvl2?.discord_id || lvl1_person?.discord_id || null,
    leadLine: lvl2?.line_id || lvl1_person?.line_id || null,
    leadTiktok: lvl2?.tiktok_id || lvl1_person?.tiktok_id || null,
    leadWechat: lvl2?.wechat_id || lvl1_person?.wechat_id || null,
    leadWhatsapp: lvl2?.whatsapp_id || lvl1_person?.whatsapp_id || null,
    leadMessenger: lvl2?.messenger_id || lvl1_person?.messenger_id || null,
    leadGooglePlaces: lvl2?.google_places_id || lvl1_person?.google_places_id || null,
    leadTripadvisor: lvl2?.tripadvisor_id || lvl1_person?.tripadvisor_id || null,
    leadThreads: lvl2?.threads_id || lvl1_person?.threads_id || null,

    // ========================================================================
    // COMPANY BASIC INFO
    // ========================================================================

    companyName: lvl1_company?.name || (lvl2 as any)?.company_name || 'Unknown',
    // FIX: lvl1_company uses website_id NOT website (see console logs)
    companyWebsite: (lvl2 as any)?.company_website_id || lvl2?.website_id || (lvl1_company as any)?.website_id || lvl1_company?.website || '',
    // FIX: lvl1_company uses email_id, phone_id NOT email/phone (same pattern as website/linkedin)
    companyEmail: (lvl2 as any)?.company_email_id || (lvl1_company as any)?.email_id || lvl1_company?.email || null,
    companyPhone: (lvl2 as any)?.company_phone_id || (lvl1_company as any)?.phone_id || lvl1_company?.phone || null,
    // VAT: Try vat_id, cif, vat_number, tax_id from various sources
    companyVat: (lvl2 as any)?.company_vat_id || (lvl1_company as any)?.vat_id || (lvl1_company as any)?.cif || (lvl1_company as any)?.vat_number || (lvl1_company as any)?.tax_id || null,
    // FIX: Add companySummary.business_profile.industry as fallback (see console logs - it has 'Transportation')
    companyIndustry: (lvl2 as any)?.company_industry || lvl1_company?.industry || lvl2?.industry || companySummary?.business_profile?.industry || 'Unknown',
    cnae_code: extractFieldValue(lvl1_company?.cnae_code || lvl2?.cnae_code),
    cnae_text: extractFieldValue(lvl1_company?.cnae_text || lvl2?.cnae_text),
    birthdate: lvl1_company?.birthdate || lvl1_company?.founded_year || null,
    continent: extractFieldValue(lvl1_company?.continent),
    size_range: extractFieldValue(lvl1_company?.size_range),
    revenue: extractFieldValue(lvl1_company?.revenue),
    business_database_summary: lvl1_company?.business_database_summary || null,
    industry: lvl1_company?.industry || lvl2?.industry || companySummary?.business_profile?.industry || 'Unknown',
    // FIX: Prioritize nr_employees (actual number) over employee_count (from companySummary)
    companyEmployees: lvl1_company?.nr_employees || lvl2?.nr_employees || companySummary?.size_and_stage?.employee_count || companySummary?.business_profile?.employee_count || 'Unknown',
    companySize: lvl1_company?.size || companySummary?.size_and_stage?.size_category || 'Unknown',
    companyStage: companySummary?.business_profile?.company_stage || lvl1_company?.stage || companySummary?.size_and_stage?.growth_stage || 'Unknown',
    revenueRange: companySummary?.business_profile?.revenue_range || companySummary?.size_and_stage?.revenue_range || 'Unknown',
    companyFounded: lvl1_company?.founded || companySummary?.company_profile?.founded || '',
    companyLogo: (() => {
      // Try multiple patterns to find company logo in Appwrite
      // Pattern 1: company ID directly (most common)
      // Pattern 2: lead ID (if N8N stores it that way)
      const companyId = lvl1_company?.id;
      const leadId = lvl2?.id || lvl1_person?.id;

      if (companyId || leadId) {
        const bucketId = import.meta.env.VITE_APPWRITE_BUCKET_LEAD_IMAGES || 'lead-images';
        // Try company ID first, fallback to lead ID
        const fileId = (companyId || leadId).toString();
        const appwriteUrl = getAppwriteStorageUrl(bucketId, fileId);

        console.log('🖼️ [COMPANY LOGO] Appwrite URL:', {
          companyId,
          leadId,
          fileId,
          bucketId,
          url: appwriteUrl
        });
        return appwriteUrl;
      }

      console.warn('⚠️ [COMPANY LOGO] No company or lead ID available');
      return '';
    })(),
    businessModel: companySummary?.company_profile?.business_model || 'Unknown',

    // ========================================================================
    // COMPANY SOCIAL MEDIA - Use company_ prefixed fields from lvl2, then fallback
    // ========================================================================

    // Company LinkedIn must come from company-owned fields only, never the person's linkedin_id.
    companyLinkedin: (lvl2 as any)?.company_linkedin_id || (lvl2 as any)?.company_linkedin_url || (lvl2 as any)?.company_linkedin || (lvl1_company as any)?.linkedin_id || lvl1_company?.linkedin_url || null,
    companyTwitter: (lvl2 as any)?.company_twitter_x_id || lvl2?.twitter_x_id || (lvl1_company as any)?.twitter_id || lvl1_company?.twitter_url || null,
    companyFacebook: (lvl2 as any)?.company_facebook_id || lvl2?.facebook_id || (lvl1_company as any)?.facebook_id || lvl1_company?.facebook_url || null,
    companyInstagram: (lvl2 as any)?.company_instagram_id || lvl2?.instagram_id || (lvl1_company as any)?.instagram_id || lvl1_company?.instagram_url || null,
    companyYoutube: (lvl2 as any)?.company_youtube_id || lvl2?.youtube_id || (lvl1_company as any)?.youtube_id || lvl1_company?.youtube_url || null,
    companyDiscord: (lvl2 as any)?.company_discord_id || lvl2?.discord_id || null,
    companyLine: (lvl2 as any)?.company_line_id || lvl2?.line_id || null,
    companyTiktok: (lvl2 as any)?.company_tiktok_id || lvl2?.tiktok_id || null,
    companyWechat: (lvl2 as any)?.company_wechat_id || lvl2?.wechat_id || null,
    companyWhatsapp: (lvl2 as any)?.company_whatsapp_id || lvl2?.whatsapp_id || null,
    companyMessenger: (lvl2 as any)?.company_messenger_id || lvl2?.messenger_id || null,
    companyGooglePlaces: (lvl2 as any)?.company_google_places_id || lvl2?.google_places_id || null,
    companyTripadvisor: (lvl2 as any)?.company_tripadvisor_id || lvl2?.tripadvisor_id || null,

    // ========================================================================
    // LOCATION DATA
    // ========================================================================

    companyCountry: lvl1_company?.country || lvl2?.country || lvl1_person?.country || '',
    companyCity: lvl1_company?.city || lvl2?.city || lvl1_person?.city || '',
    companyState: lvl1_company?.state || lvl2?.state || lvl1_person?.state || '',
    companyAddress: lvl1_company?.address || '',
    fullAddress: lvl1_company?.full_address || '',
    companyLat: lvl1_company?.latitude || null,
    companyLon: lvl1_company?.longitude || null,
    postalCode: lvl1_person?.postal_code || '',
    neighborhood: lvl1_person?.neighborhood || '',

    // ========================================================================
    // PROFESSIONAL PROFILE (from parsed summary - CRITICAL FIX)
    // ========================================================================

    decisionAuthority,
    influenceLevel,
    budgetControl,
    professionalFocus,
    yearsExperience,
    careerStage,
    linkedinFollowers,
    // FIX: Extract from nested parsed personSummary paths
    skills: personSummary?.professional_profile?.skills || professionalProfile.skills || [],
    keyResponsibilities: personSummary?.professional_position?.key_responsibilities || professionalProfile.key_responsibilities || [],

    // ========================================================================
    // PSYCHOLOGICAL PROFILE
    // ========================================================================

    communicationStyle: psychologicalProfile.communication_style || 'Unknown',
    decisionStyle: psychologicalProfile.decision_style || 'Unknown',
    riskTolerance: psychologicalProfile.risk_tolerance || 'Unknown',

    // ========================================================================
    // ICP & SCORES
    // ========================================================================

    icpScore: lvl2?.icp_score || 0,
    profileCompleteness: lvl2?.profile_completeness || lvl1_person?.profile_completeness || 0,

    // ========================================================================
    // SUMMARIES & OVERVIEWS
    // ========================================================================

    leadExecutiveOverview: lvl2?.overview || personSummary?.executive_summary || 'No summary available.',
    companyExecutiveOverview: companySummary?.executive_overview || companyIntel?.executive_overview || 'No company summary available.',
    companyGeneratorSummary: companySummary?.executive_overview || 'No details available.',
    generatorSummary: lvl2?.overview || personSummary?.executive_summary || 'No summary available.',
    linkedinSummary: (() => {
      // ⚠️ CRITICAL FIX: Only use PERSON data sources - exclude company data to prevent company info in Person tab
      const fromLvl1Person = lvl1_person?.linkedin_summary;
      const fromLvl2 = lvl2?.linkedin_summary;

      const summary = fromLvl1Person || fromLvl2 || '';

      console.log('✅ Setting linkedinSummary in MappedLeadData:', {
        source: fromLvl1Person ? 'lvl1_person' : fromLvl2 ? 'lvl2' : 'NONE',
        exists: !!summary,
        length: summary.length,
        preview: summary.substring(0, 200)
      });
      return summary;
    })(), // ⚠️ FIXED: Only check person sources (lvl1_person → lvl2), exclude lvl1_company to prevent company data in Person tab
    researchSummary: lvl2?.research_summary || lvl1_person?.research_summary || n8nLead?.research_summary || null,
    // Normalize lead language to a valid BCP 47 tag (single authority for lead language).
    language: getLanguageName(normalizeLocale(lvl2?.preferred_language || lvl1_person?.preferred_language)),
    languageCode: normalizeLocale(lvl2?.preferred_language || lvl1_person?.preferred_language),

    // ========================================================================
    // OPPORTUNITIES & PAIN POINTS
    // ========================================================================

    opportunities: opportunities || [],
    campaignOpportunities: Array.isArray(campaignOpportunities) ? campaignOpportunities : [],
    generalOpportunities: Array.isArray(opportunities) ? opportunities : [],
    painPoints: companySummary?.pain_points || [],
    targetAudience: lvl2?.target_audience || null,
    // FIX: Extract differentiators - prioritize market_position paths (console analysis confirmed)
    differentiationFactors: companySummary?.market_position?.differentiation_factors ||
                           companySummary?.market_position?.competitive_advantages ||
                           companySummary?.differentiation_factors ||
                           companySummary?.competitive_advantages ||
                           companySummary?.unique_value_props ||
                           companyIntel?.unique_value_props ||
                           [],
    // FIX: Extract key products - correct field name is key_products_services (console analysis confirmed)
    keyProductsServices: companySummary?.business_profile?.key_products_services ||
                        companySummary?.key_products_services ||
                        companySummary?.business_profile?.key_products ||
                        companySummary?.key_products ||
                        companyIntel?.key_products ||
                        [],

    // ========================================================================
    // OUTREACH SEQUENCES (30 messages extracted)
    // ========================================================================

    outreachSequences,

    // ========================================================================
    // AGENT INFO
    // ========================================================================

    agentName: 'Brainiacs AI Agent',
    agentPicture: 'https://appwrite-prod-u12020.vm.elestio.app/v1/storage/buckets/6940e6c3002eddae9d6b/files/6940e6ce0012ce4b11e3/view?project=693c0579001e8b06c6d6',

    // ========================================================================
    // INTERACTIONS & ACTIVITIES
    // ========================================================================

    interactions: [],
    isrNotes: parsedISRNotes.document,
    isrNotesParseError: parsedISRNotes.parseError,
    recentActivities: recentActivities || [],

    // ========================================================================
    // PROFILE PICTURE - CRITICAL FIX: Only use Appwrite Storage URLs
    // ========================================================================

    profilePicture: (() => {
      // ALWAYS construct Appwrite URL using person ID - all images are in bucket
      const personId = lvl1_person?.id;
      if (personId) {
        const bucketId = 'lead-images'; // VITE_APPWRITE_BUCKET_PROFILE_PICTURES
        const fileId = personId.toString(); // Use just the ID, no prefix
        const appwriteUrl = getAppwriteStorageUrl(bucketId, fileId);
        console.log('🔧 Constructed Appwrite profile picture URL:', {
          personId,
          fileId,
          url: appwriteUrl
        });
        return appwriteUrl;
      }

      console.warn('⚠️ No person ID available for profile picture');
      return '';
    })(),
    profilePictureStorageUrl: (() => {
      const personId = lvl1_person?.id;
      return personId ? getAppwriteStorageUrl('lead-images', personId.toString()) : null;
    })(),

    // ========================================================================
    // RAW DATA PRESERVATION (backward compatibility)
    // ========================================================================

    rawProfileIntel: profileIntel,
    rawCompanySummary: companySummary,
    rawLeadSummary: personSummary,
    rawOriginalData: n8nLead,

    // Preserve raw N8N levels for components that access them
    personLvl2: lvl2 || null,
    personLvl1: lvl1_person || null,
    companyLvl1: lvl1_company || null,
    // Extract executive overview from multiple possible locations
    // Priority: lvl2.overview > executive_summary.executive_overview
    /** Parse the lvl1_company email_provider JSON column — domain-level email
     *  validation data (provider, deliverability risk, role-account detection).
     *  Parsed via safeParse since the column is stored as a JSON string. */
    emailProviderData: safeParse(lvl1_company?.email_provider) || null,
    /** Per-channel enrichment timestamps from lvl2 — a truthy `xx_enriched`
     *  value means Brainiacs verified that channel during enrichment. Powers
     *  the VERIFIED pills on contact rows. */
    channelEnrichedAt: extractChannelEnrichedAt(lvl2),
    personLvl2Overview: lvl2?.overview ||
                          lvl2?.executive_summary?.executive_overview ||
                          null,
    personLvl2PersonalizationStrategy: lvl2?.personalization_strategy || null,
    companySummary: companySummary || null,
    profileIntelligence: profileIntel || null,
  };

  // DEBUG: Log transformed data to verify mapping
  console.log('🔍 transformN8NToMappedLead() OUTPUT:', {
    leadId: result.id,
    campaignId: result.campaignId,

    // Core identity fields
    leadName: result.leadName,
    leadRole: result.leadRole,
    email: result.email,

    // Company fields
    companyName: result.companyName,
    companyIndustry: result.companyIndustry,
    companyWebsite: result.companyWebsite,

    // ICP scores (camelCase!)
    icpScore: result.icpScore,

    // Professional fields that were missing
    decisionAuthority: result.decisionAuthority,
    influenceLevel: result.influenceLevel,
    budgetControl: result.budgetControl,
    professionalFocus: result.professionalFocus,

    // Sample of 20 key UI fields
    samples: {
      leadName: result.leadName,
      leadRole: result.leadRole,
      leadLinkedin: result.leadLinkedin,
      companyLinkedin: result.companyLinkedin,
      companyLogo: result.companyLogo,
      companyWebsite: result.companyWebsite,
      companyCountry: result.companyCountry,
      companyCity: result.companyCity,
      companyEmployees: result.companyEmployees,
      targetAudience: result.targetAudience,
      keyProductsServices: result.keyProductsServices,
      differentiationFactors: result.differentiationFactors,
      communicationStyle: result.communicationStyle,
      riskTolerance: result.riskTolerance,
      linkedinFollowers: result.linkedinFollowers,
      yearsExperience: result.yearsExperience,
      careerStage: result.careerStage,
      skills: result.skills,
      keyResponsibilities: result.keyResponsibilities,
      companyGeneratorSummary: result.companyGeneratorSummary?.substring(0, 100),
      generatorSummary: result.generatorSummary?.substring(0, 100),
      outreachSequencesCount: result.outreachSequences?.length,
      rawCompanySummaryKeys: Object.keys(result.rawCompanySummary || {}),
      rawProfileIntelKeys: Object.keys(result.rawProfileIntel || {}),
      personLvl2Keys: Object.keys(result.personLvl2 || {}),
    },
    // DEBUG: Show source values for verification
    sources: {
      leadName: { lvl2: lvl2?.name, lvl1_person: lvl1_person?.name },
      leadRole: { lvl2: lvl2?.role_position, lvl1_person: lvl1_person?.role_position },
      companyEmployees: { lvl2_company_size: lvl2?.company_size, lvl1_company_size: lvl1_company?.company_size },
    }
  });

  return result;
}
